import {HttpClient, HttpHeaders, HttpParams} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {EMPTY, Observable} from 'rxjs';
import {map} from 'rxjs/internal/operators';

import * as uuid from 'uuid/v4';
import {environment} from '../../environments/environment';
import {Endpoints} from '../config/endpoints';
import {APP_VERSION} from '../config/version';
import {Approval} from '../models/approval';
import {Attendance} from '../models/attendance';
import {Customer} from '../models/customer';
import {CustomerDeliveryChallan} from '../models/customer-delivery-challan';
import {CustomerInvoice} from '../models/customer-invoice';
import {CustomerPayment} from '../models/customer-payment';
import {CustomerReturn} from '../models/customer-return';
import {CustomerType} from '../models/customer-type';
import {DailyTaDa} from '../models/DailyTaDa';
import {DailyTaDaClaim} from '../models/DailyTaDaClaim';
import {Distributor} from '../models/distributor';
import {DistributorBankAccount} from '../models/distributor-bank-account';
import {DistributorDeliveryChallan} from '../models/distributor-delivery-challan';
import {DistributorInvoice} from '../models/distributor-invoice';
import {DistributorPayment} from '../models/distributor-payment';
import {DistributorReturn} from '../models/distributor-return';
import {DistributorStock} from '../models/distributor-stock';
import {DistributorType} from '../models/distributor-type';
import {ExpenseClaim} from '../models/expense-claim';
import {ExpensePurpose} from '../models/expense-purpose';
import {ExpenseType} from '../models/expense-type';
import {Holiday} from '../models/holiday';
import {LeaveAllocation} from '../models/leave-allocation';
import {LeaveType} from '../models/leave-type';
import {Market} from '../models/market';
import {MarketLevel} from '../models/market-level';
import {Module} from '../models/module';
import {Organization} from '../models/organization';
import {OrganizationBankAccount} from '../models/organization-bank-account';
import {OrganizationSettings} from '../models/organization-settings';
import {PaymentMethod} from '../models/payment-method';
import {PaymentPurpose} from '../models/payment-purpose';
import {PaymentCategory} from '../models/payment-category';
import {PrimaryOrder} from '../models/primary-order';
import {Product} from '../models/product';
import {ProductBrand} from '../models/product-brand';
import {ProductCampaign} from '../models/product-campaign';
import {ProductGroup} from '../models/product-group';
import {ProductLine} from '../models/product-line';
import {ProductType} from '../models/product-type';
import {SecondaryOrder} from '../models/secondary-order';
import {Tag} from '../models/tag';
import {TourPlan} from '../models/tour-plan';
import {TourPlanUser} from '../models/tour-plan-user';
import {TourType} from '../models/tour-type';
import {TrackingLocation} from '../models/tracking-location';
import {User} from '../models/user';
import {UserLeave} from '../models/user-leave';
import {UserLeaveAccount} from '../models/user-leave-account';
import {UserRole} from '../models/user-role';
import {DailyShift} from '../models/daily-shift';
import {PrimaryOrderTracking} from '../models/primary-order-tracking';
import {ProductGroupStockSummary} from '../models/product-group-stock-summary';
import {PersonWiseMonthlyKpi} from '../models/person-wise-monthly-kpi';
import {BillingOrganization} from '../models/BillingOrganization';
import {VisitType} from 'app/models/visit-type';
import {VisitStatus} from '../models/visit-status';
import {Visit} from '../models/visit';
import {NoticeBoard} from '../models/notice-board';
import {CreditOption} from '../models/credit-option';
import {SafetyStock} from '../models/safety-stock';
import {CustomerCampaign} from '../models/customer-campaign';
import {DistributorCampaign} from '../models/distributor-campaign';
import {SecondaryOrderTracking} from '../models/secondary-order-tracking';
import {DistributorCenter} from '../models/distributor-centre';
import {CustomerReturnReason} from '../models/customer-return-reason';
import {DistributorReturnReason} from '../models/distributor-return-reason';
import {Fil} from '../models/fil';
import {DistributorDeliveryChallanByProduct} from '../models/distributor-delivery-challan-by-product';
import {CustomerDeliveryChallanByProduct} from '../models/customer-delivery-challan-by-product';
import {LeaveAccount} from '../models/leave-account';
import {ProjectOwnerType} from '../models/project-owner-type';
import {ProjectType} from '../models/project-type';
import {Project} from '../models/project';
import {ProjectStatus} from '../models/project-status';
import {ExpenseClaimApprovalRules} from '../models/expense-claim-approval-rules';
import {ProjectConsultant} from '../models/project-consultant';
import {ProjectDemandDocumentType} from '../models/project-demand-document-type';
import {ProjectDemand} from '../models/project-demand';
import {CentralStock} from '../models/central-stock';
import {TransferFromCentralStock} from '../models/transfer-from-central-stock';
import {ProjectProperties} from '../models/project-properties';
import {ProjectPropertiesValue} from '../models/project-properties-value';
import {ExpenseSubType} from '../models/expense-sub-type';
import {Password} from '../models/password';
import {VisitSummaryApprovalRule} from '../models/visit-summary-approval-rule';
import {TransportMode} from '../models/transport-mode';
import {ItemWiseTourApproval} from '../models/item-wise-tour-approval';
import {TourPlanTarget} from '../models/tour-plan-target';
import {DealerType} from '../models/dealer-type';
import {Dealer} from '../models/dealer';
import {DealerPayment} from '../models/dealer-payment';
import {DistributorDealerOrder} from '../models/distributor-dealer-order';
import {DistributorDealerInvoice} from '../models/distributor-dealer-invoice';
import {DistributorDealerDelivery} from '../models/distributor-dealer-delivery';
import {RetailerToDealerOrder} from '../models/retailer-to-dealer-order';
import {RetailerToDealerInvoice} from '../models/retailer-to-dealer-invoice';
import {RetailerToDealerDelivery} from '../models/retailer-to-dealer-delivery';
import {NotificationMinute} from '../models/notification-minute';
import {RetailerToDealerCampaign} from '../models/retailer-to-dealer-campaign';
import {Weekend} from '../models/weekend';
import {UserStatusChange} from '../models/user-status-change';
import {ProductStatusChange} from '../models/product-status-change';
import {UserRoleWiseVisitTarget} from '../models/user-role-wise-visit-target';
import {DealerCampaign} from '../models/dealer-campaign';
import {SampleRequisition} from '../models/sample-requisition';
import {SampleRequisitionApproval} from '../models/sample-requisition-approval';
import {RetailerMonthlyNoc} from '../models/retailer-monthly-noc';
import {Engagement} from '../models/engagement';
import {Segment} from '../models/segment';
import {SubSegment} from '../models/sub-segment';
import {Division} from '../models/division';
import {District} from '../models/district';
import {Thana} from '../models/thana';
import {UnionOrTown} from '../models/union-or-town';
import {Level} from '../models/level';
import {Training} from '../models/training';
import {Scheme} from '../models/scheme';
import {NotificationRule} from '../models/notification-rule';
import {KpiTargetAchievement} from '../models/kpi-target-achievement';
import {RetailerKpiTargetAchievement} from '../models/RetailerKpiTargetAchievement';
import {UserTimeline} from '../models/user-timeline';
import {TourAllowanceRule} from '../models/tour-allowance-rule';
import {Poi} from '../models/poi';
import {Type} from '../models/type';
import {MarketTargetReport} from '../models/market-target-report';
import {MarketTargetSet} from '../models/market-target-set';
import {Bank} from '../models/bank';
import {BankUpload} from '../models/bank-upload';
import {CodeGeneratorRule} from '../models/code-generator-rule';
import {MonitoringUserLastLocations} from '../models/monitoring-user-last-locations';
import {MonitorUserTimeline} from '../models/monitor-user-timeline';
import {NoticeBoardForOthers} from '../models/notice-board-for-others';
import {base_urls} from '../config/base_urls';
import {UserRoleForMicroServiceList} from '../models/user-role-for-micro-service-list';
import {UserRoleForMicroServiceManage} from '../models/user-role-for-micro-service-manage';
import {NoticeBoardForOthersManage} from '../models/notice-board-for-others-manage';
import {MarketRef} from '../models/market-ref';
import {UserRolePermissionList} from '../models/user-role-permission-list';
import {UserRolePermissionManage} from '../models/user-role-permission-manage';
import {ActivationStatus} from 'app/models/activation-status';
import {IncomeManage} from '../models/income-manage';
import {ApprovalForMicroService} from '../models/approval-for-micro-service';
import {SecondaryOrderRequest} from '../models/secondary-order-request';
import {SecondaryOrderPreview} from '../models/secondary-order-preview';
import {SecondaryOrderDetails} from '../models/secondary-order-details';
import {ApprovalRuleRequest} from '../models/approval-rule-dto';
import {Fridge} from '../models/fridge';
import {DaRuleManage} from '../models/da-rule-manage';
import {TaClaim} from '../models/ta-claim';
import {TaForSubordinates} from '../models/ta-for-subordinates';
import {TriggerDa} from '../models/trigger-da';
import {AppliedDa} from '../models/applied-da';
import {StockSurvey} from '../models/stock-survey';
import {PjpPlan, PjpPlanRequest, PjpPlanRequestForSingleTeam} from '../models/pjp-details';
import {RoutePlan, RoutePlanRequest} from '../models/route-plan';
import {
    CustomerInvoiceAfterDeliveryRequestDto,
    CustomerInvoiceDto,
    CustomerInvoiceItemRequestDto,
    CustomerInvoicePreview,
    CustomerInvoiceRequestDto
} from '../models/customer-invoice-v2';
import {MarketProductTarget} from '../models/market-product-target';
import { MovementApprove} from '../models/movement';
import {UserRoleRef} from '../models/user-role-ref';
import {MarketProductTargetUpload} from '../models/market-product-target-upload';
import {Ims, Sr} from '../models/ims';
import {MarketKpiByPlanVisit} from '../models/market-kpi-by-plan-visit';
import {AssetType} from '../models/asset-type';
import {Asset} from '../models/asset';
import {AssetRetailerUpload} from '../models/assetRetailerUpload';
import {ImsDetailsReport} from '../models/ims-report/ims-details-report';
import {ImsDailySummaryByVolume} from '../models/ims-daily-summary-by-volume';
import {GeneralReturn} from '../models/general-return';
import { AppConfigService } from './app-config.service';
import {SalesKpiByDashboard} from '../models/sales-kpi-by-dashboard';
import {WeeklyBuyerOutletTotal} from '../models/weekly-buyer-outlet/weekly-buyer-outlet-total';

@Injectable({providedIn: 'root'})
export class BaseService {

    poiTypes: Type[] = [];

    constructor(private http: HttpClient, private config: AppConfigService) {
    }

    get accessToken(): string {
        return localStorage.getItem('access_token');
    }

    get refreshToken(): string {
        return localStorage.getItem('refresh_token');
    }

    get uuid(): string {
        return localStorage.getItem('uuid') || this._generateUUID();
    }

    _generateUUID(): string {
        const uuidString = uuid();
        localStorage.setItem('uuid', uuidString);
        return uuid;
    }

    _generateQueryStringFromObject(query: any): string {
        const queryString = Object.keys(query).map(key => {
                if (query[key] != null) {
                    if (typeof query[key] === 'object') {
                        const singleArray = [];
                        for (const singleArrIndex of query[key]) {
                            singleArray.push(singleArrIndex);
                        }
                        return key + '=' + singleArray.join(',') + '&';
                    } else {
                        return key + '=' + query[key] + '&';
                    }

                } else {
                    return '';
                }
            }
        ).join('');
        return queryString;
    }

    generateGetUrl(apiEndpoint: string, data?: any, fileName?: string, includeToken: boolean = true): string {
        return this.config.apiUrl + base_urls.sfa_url + apiEndpoint + '?' + (includeToken ? 'access_token=' + this.accessToken + '&' : '') + (fileName ? 'DownloadFileName=' + fileName + '&' : '') + (data ? this._generateQueryStringFromObject(data) : '');
    }

    generateGetUrlForMicroservice(apiEndpoint: string, data?: any, fileName?: string, includeToken: boolean = true): string {
        return this.config.apiUrl + base_urls.f_service_income + apiEndpoint + '?' + (includeToken ? 'access_token=' + this.accessToken + '&' : '') + (fileName ? 'downloadFileName=' + fileName + '&' : '') + (data ? this._generateQueryStringFromObject(data) : '');
    }

    generateMicroServiceGetUrl(apiEndpoint: string, data?: any, fileName?: string, includeToken: boolean = true): string {
        return this.config.apiUrl + apiEndpoint + '?' + (includeToken ? 'access_token=' + this.accessToken + '&' : '') + (fileName ? 'downloadFileName=' + fileName + '&' : '') + (data ? this._generateQueryStringFromObject(data) : '');
    }

    generateAsyncGetUrl(apiEndpoint: string, data?: any, fileName?: string, includeToken: boolean = true): Observable<any> {
        return this.getData(apiEndpoint + '?' + (includeToken ? 'access_token=' + this.accessToken + '&' : '')
            + (fileName ? 'DownloadFileName=' + fileName + '&' : '') + (data ? this._generateQueryStringFromObject(data) : ''))
            .pipe(map(response => response['Data']));
    }

    generateAsyncGetUrlForMicroservice(apiEndpoint: string, data?: any, fileName?: string, includeToken: boolean = true): Observable<any> {
        return this.getDataForMicroService(apiEndpoint + '?' + (includeToken ? 'access_token=' + this.accessToken + '&' : '')
            + (fileName ? 'downloadFileName=' + fileName + '&' : '') + (data ? this._generateQueryStringFromObject(data) : ''))
            .pipe(map(response => response['data']));
    }

    getHeaders(includeToken: boolean = true): HttpHeaders {
        // const headers = { 'Content-Type': 'application/json' };
        const headers = {};
        if (includeToken) {
            headers['Authorization'] = `Bearer ${this.accessToken}`;
            headers['client_uuid'] = this.uuid;
            headers['client_type'] = 'BROWSER';
            headers['app_version_name'] = APP_VERSION;
        } else {
            headers['Authorization'] = 'Basic bXktdHJ1c3RlZC1jbGllbnQ6c2VjcmV0';
            headers['Content-Type'] = 'application/x-www-form-urlencoded';
        }
        return new HttpHeaders(headers);
    }


    getData(apiEndpoint: string, data?: any, includeToken: boolean = true): Observable<any> {
        return this.http.get(this.config.apiUrl + base_urls.sfa_url + apiEndpoint, {
            params: new HttpParams({fromObject: data}),
            headers: this.getHeaders(includeToken)
        });
    }

    getDataForMicroService(apiEndpoint: string, data?: any, includeToken: boolean = true): Observable<any> {
        if (data) {
            data.access_token = this.accessToken;
        }
        return this.http.get(this.config.apiUrl + apiEndpoint, {
            params: new HttpParams({fromObject: data}),
            headers: this.getHeaders(includeToken)
        });
    }

    getFile(apiEndpoint: string, data?: any, includeToken: boolean = true): Observable<any> {
        return this.http.get(apiEndpoint, {
            params: new HttpParams({fromObject: data}),
            headers: this.getHeaders(includeToken)
        });
    }

    postData(apiEndpoint: string, data: any, includeToken: boolean = true): Observable<any> {
        return this.http.post(this.config.apiUrl + base_urls.sfa_url + apiEndpoint, data, {headers: this.getHeaders(includeToken)});
    }

    postDataForMicroService(apiEndpoint: string, data: any, includeToken: boolean = true): Observable<any> {
        return this.http.post(this.config.apiUrl + apiEndpoint, data, {
            params: new HttpParams({fromObject: {access_token: this.accessToken}}),
            headers: this.getHeaders(includeToken)
        });
    }

    putDataForMicroService(apiEndpoint: string, data?: any, includeToken: boolean = true): Observable<any> {
        return this.http.put(this.config.apiUrl + apiEndpoint, data, {
            params: new HttpParams({fromObject: {access_token: this.accessToken}}),
            headers: this.getHeaders(includeToken)
        });
    }

    patchData(apiEndpoint: string, data: any, includeToken: boolean = true): Observable<any> {
        return this.http.patch(this.config.apiUrl + apiEndpoint, data, { headers: this.getHeaders(includeToken) });
    }

    putData(apiEndpoint: string, data: any, includeToken: boolean = true): Observable<any> {
        return this.http.put(this.config.apiUrl + base_urls.sfa_url + apiEndpoint, data, {headers: this.getHeaders(includeToken)});
    }

    deleteData(apiEndpoint: string, data?: any, includeToken: boolean = true): Observable<any> {
        return this.http.delete(this.config.apiUrl + base_urls.sfa_url + apiEndpoint, {
            params: new HttpParams({fromObject: data}),
            headers: this.getHeaders(includeToken)
        });
    }

    deleteDataForMicroservice(apiEndpoint: string, includeToken: boolean = true): Observable<any> {
        return this.http.delete(this.config.apiUrl + apiEndpoint, {
            headers: this.getHeaders(includeToken)
        });
    }


    getModuleListForOrganization(pParameters: any): Observable<Module[]> {
        return this.postData(Endpoints.organization_modules, pParameters).pipe(map(response => response['Data']['content']));
    }


    getOrganizationList(pParameters: any): Observable<Organization[]> {
        return this.postData(Endpoints.organization_list, pParameters).pipe(map(response => response['Data']['content']));
    }

    getOrganizationDetails(pOrganizationId: number): Observable<Organization> {
        return this.getData(`${Endpoints.organization_details}/${pOrganizationId}`).pipe(map(response => response['Data']['Organization']));
    }

    saveOrganization(pOrganization: any): Observable<any> {
        return this.postData(pOrganization.Id ? Endpoints.organization_edit : Endpoints.organization_add, pOrganization);
    }

    changeOrganizationStatus(pApproval: ActivationStatus, pURL: string): Observable<any> {
        return this.postDataForMicroService(pURL + Endpoints.organization_status_update, pApproval);
    }

    addBillPendingOrganization(pOrganizationId: any): Observable<any> {
        return this.postData(Endpoints.add_bill_pending_organization, pOrganizationId);
    }

    deleteBillPendingOrganization(pOrganizationId: number): Observable<any> {
        return this.deleteData(`${Endpoints.delete_bill_pending_organization}/${pOrganizationId}`);
    }

    setRestrictionMessage(pMessage: any): Observable<any> {
        return this.postData(Endpoints.set_restriction_message, pMessage);
    }

    getRestrictionMessage(pMessage: any): Observable<any> {
        return this.getData(Endpoints.get_restriction_message, pMessage).pipe(map(response => response['Data']['Message']));
    }

    saveChangedPassword(pParameters: Password): Observable<any> {
        return this.postData(Endpoints.change_password, pParameters).pipe(map(response => response['Data']['content']));
    }

    deleteOrganization(pOrganizationId: number): Observable<any> {
        return this.deleteData(`${Endpoints.organization_delete}/${pOrganizationId}`);
    }

    getOrganizationSettings(pOrganizationId?: number): Observable<OrganizationSettings[]> {
        return this.postData(Endpoints.organization_settings, {'OrganizationId': pOrganizationId ? pOrganizationId : null})
            .pipe(map(response => response['Data']['content']));
    }

    getAndroidAppInfo(pOrganizationCode: string): Observable<any> {
        return this.getData(`${Endpoints.app_update_info}` + '?' + ('orgCode=' + pOrganizationCode)).pipe(map(response => response['Data']['Organization']));
    }

    updateAndroidAppInfo(pOrganization: any): Observable<any> {
        return this.postData(Endpoints.app_update, pOrganization);
    }

    updateDistributorAppInfo(pOrganization: any): Observable<any> {
        return this.postData(Endpoints.distributor_app_update, pOrganization);
    }

    // dashboard
    getDashboardCustomerDailyOrderDummy(pParameters: any): Observable<any> {
        return this.postData(Endpoints.dashboard_customer_dummy_order, pParameters).pipe(map(response => response['Data']['DailyStatus']));
    }

    getDashboardRetailerToDealerOrder(pParameters: any): Observable<any> {
        return this.postData(Endpoints.dashboard_retailer_to_dealer_order, pParameters).pipe(map(response => response['Data']['DailyStatus']));
    }

    getDashboardCustomerOrder(pParameters: any): Observable<any> {
        return this.postData(Endpoints.dashboard_customer_order, pParameters).pipe(map(response => response['Data']['DailyStatus']));
    }

    getDashboardBrandSalesDummy(pParameters: any): Observable<any> {
        return this.postData(Endpoints.dashboard_brand_sales_dummy, pParameters).pipe(map(response => response['Data']['content']));
    }

    getDashboardHitCount(pParameters: any): Observable<any> {
        return this.getData(Endpoints.dashboard_hit_count, pParameters).pipe(map(response => response['Data']['List']));
    }

    getDashboardRoleWisePresenceCount(pParameters: any): Observable<any> {
        return this.getData(Endpoints.dashboard_role_wise_presence_count, pParameters).pipe(map(response => response['Data']['content']));
    }

    getDashboardMarketWisePresenceCount(pParameters: any): Observable<any> {
        return this.getData(Endpoints.dashboard_market_presence_count, pParameters).pipe(map(response => response['Data']['content']));
    }

    getDashboardOrderSummary(pParameters: any): Observable<any> {
        return this.getData(Endpoints.dashboard_order_summary, pParameters).pipe(map(response => response['Data']['Info']));
    }

    getDashboardSecondaryOrderSummaryByProduct(pParameters: any): Observable<any> {
        return this.getData(Endpoints.dashboard_order_summary_by_product, pParameters).pipe(map(response => response['Data']));
    }

    getDashboardMarketWiseOrderSummary(pParameters: any): Observable<any> {
        return this.getData(Endpoints.dashboard_market_wise_order_summary, pParameters).pipe(map(response => response['Data']['content']));
    }

    getDashboardShopCount(pParameters: any): Observable<any> {
        return this.getData(Endpoints.dashboard_planned_retailer_count, pParameters).pipe(map(response => response['Data']['Count']));
    }

    getDashboardSRCount(pParameters: any): Observable<any> {
        return this.getData(Endpoints.dashboard_user_count, pParameters).pipe(map(response => response['Data']['Count']));
    }

    getDashboardShopVisitCount(pParameters: any): Observable<any> {
        return this.getData(Endpoints.dashboard_visited_retailer_count, pParameters).pipe(map(response => response['Data']['Count']));
    }

    getDashboardPresentSRCount(pParameters: any): Observable<any> {
        return this.getData(Endpoints.dashboard_user_present_count, pParameters).pipe(map(response => response['Data']['Info']));
    }

    getDashboardMemoCount(pParameters: any): Observable<any> {
        return this.getData(Endpoints.dashboard_ordered_retailer_count, pParameters).pipe(map(response => response['Data']['Count']));
    }

    getDashboardTodaysTotalOrder(pParameters: any): Observable<any> {
        return this.getData(Endpoints.dashboard_total_orders_of_day, pParameters).pipe(map(response => response['Data']['Info']));
    }

    getDashboardTotalMonthlyOrderTarget(pParameters: any): Observable<any> {
        return this.getData(Endpoints.dashboard_secondary_order_target_of_month, pParameters).pipe(map(response => response['Data']['Total']));
    }

    getDashboardTotalMonthlyOrderAchievement(pParameters: any): Observable<any> {
        return this.getData(Endpoints.dashboard_secondary_order_achievement_of_month, pParameters).pipe(map(response => response['Data']['Total']));
    }

    getDashboardMarketDailyKpi(pParameters: any): Observable<any> {
        return this.postData(Endpoints.dashboard_market_daily_kpi, pParameters).pipe(map(response => response['Data']['content']));
    }

    getDashboardMarketDailyKpiByPlanVisit(pParameters: any, pURL): Observable<MarketKpiByPlanVisit[]> {
        return this.postDataForMicroService(pURL + Endpoints.dashboard_market_daily_kpi_by_plan_visit, pParameters).pipe(map(response => response['data']['content']));
    }

    getDashboardSalesKpi(pParameters: any, pURL): Observable<SalesKpiByDashboard> {
        return this.postDataForMicroService(pURL + Endpoints.dashboard_sales_kpi, pParameters).pipe(map(response => response['data']));
    }

    getDashboardAverageVisitTime(pParameters: any): Observable<any> {
        return this.getData(Endpoints.dashboard_average_visit_time, pParameters).pipe(map(response => response['Data']['Duration']));
    }

    getDashboardPaymentSummary(pParameters: any): Observable<any> {
        return this.getData(Endpoints.dashboard_payment_summary, pParameters).pipe(map(response => response['Data']['Total']));
    }

    getDashboardMarketWisePaymentSummary(pParameters: any): Observable<any> {
        return this.getData(Endpoints.dashboard_market_wise_payment_summary, pParameters).pipe(map(response => response['Data']['content']));
    }

    getDistributorOrderInvoicePayment(pParameters: any): Observable<any> {
        return this.postData(Endpoints.dashboard_distributor_order_invoice_payment, pParameters).pipe(map(response => response['Data']['DailyStatus']));
    }

    getDealerOrderSummary(pParameters: any): Observable<any> {
        return this.postData(Endpoints.dashboard_dealer_order_summary, pParameters).pipe(map(response => response['Data']['Summary']));
    }

    getDealerInvoiceSummary(pParameters: any): Observable<any> {
        return this.postData(Endpoints.dashboard_dealer_invoice_summary, pParameters).pipe(map(response => response['Data']['Summary']));
    }

    getRetailerOrderSummary(pParameters: any): Observable<any> {
        return this.postData(Endpoints.dashboard_retailer_order_summary, pParameters).pipe(map(response => response['Data']['Summary']));
    }

    getRetailerInvoiceSummary(pParameters: any): Observable<any> {
        return this.postData(Endpoints.dashboard_retailer_invoice_summary, pParameters).pipe(map(response => response['Data']['Summary']));
    }

    getMarketSalesTarget(pParameters: any): Observable<any> {
        return this.postData(Endpoints.dashboard_market_sales_target, pParameters).pipe(map(response => response['Data']['content']));
    }

    getMonthlyRetailerOrderVsVisitChart(pParameters: any): Observable<any> {
        return this.postData(Endpoints.dashboard_monthly_retailer_order_vs_visit, pParameters).pipe(map(response => response['Data']['content']));
    }

    getMonthlyRetailerOrderVsInvoiceChart(pParameters: any): Observable<any> {
        return this.postData(Endpoints.dashboard_monthly_retailer_order_vs_invoice, pParameters).pipe(map(response => response['Data']['content']));
    }

    getSalesTargetByProductBrand(pParameters: any): Observable<any> {
        return this.postData(Endpoints.dashboard_sales_target_by_product_brand, pParameters).pipe(map(response => response['Data']['content']));
    }

    getSalesTargetByProductGroup(pParameters: any): Observable<any> {
        return this.postData(Endpoints.dashboard_sales_target_by_product_group, pParameters).pipe(map(response => response['Data']['content']));
    }

    getSalesTargetByProductLine(pParameters: any): Observable<any> {
        return this.postData(Endpoints.dashboard_sales_target_by_product_line, pParameters).pipe(map(response => response['Data']['content']));
    }

    getSalesTargetByProductType(pParameters: any): Observable<any> {
        return this.postData(Endpoints.dashboard_sales_target_by_product_type, pParameters).pipe(map(response => response['Data']['content']));
    }

    getCustomerVisitSummary(pParameters: any): Observable<any> {
        return this.postData(Endpoints.dashboard_customer_visit_summary, pParameters).pipe(map(response => response['Data']['Summary']));
    }

    getDistributorVisitSummary(pParameters: any): Observable<any> {
        return this.postData(Endpoints.dashboard_distributor_visit_summary, pParameters).pipe(map(response => response['Data']['Summary']));
    }

    getAttendenceCount(pParameters: any): Observable<any> {
        return this.postData(Endpoints.dashboard_attendance_count_summary, pParameters).pipe(map(response => response['Data']['Count']));
    }

    getCustomerVisitCount(pParameters: any): Observable<any> {
        return this.postData(Endpoints.dashboard_customer_visit_count, pParameters).pipe(map(response => response['Data']));
    }

    getCustomerVisitCountDummy(pParameters: any): Observable<any> {
        return this.postData(Endpoints.dashboard_customer_visit_count_dummy, pParameters).pipe(map(response => response['Data']));
    }

    getMonthlySalesComparisonDummy(pParameters: any): Observable<any> {
        return this.postData(Endpoints.dashboard_monthly_sales_comparison_dummy, pParameters).pipe(map(response => response['Data']['DailySalesComparison']));
    }

    getDailySalesComparisonDummy(pParameters: any): Observable<any> {
        return this.postData(Endpoints.dashboard_daily_sales_comparison_dummy, pParameters).pipe(map(response => response['Data']['DailySalesComparison']));
    }

    getAvgSalesComparisonDummy(pParameters: any): Observable<any> {
        return this.postData(Endpoints.dashboard_avg_sales_comparison_dummy, pParameters).pipe(map(response => response['Data']['DailySalesComparison']));
    }

    getNewCustomerCount(pParameters: any): Observable<any> {
        return this.postData(Endpoints.dashboard_new_customer_count, pParameters).pipe(map(response => response['Data']));
    }

    getNewCustomerCountDummy(pParameters: any): Observable<any> {
        return this.postData(Endpoints.dashboard_new_customer_count_dummy, pParameters).pipe(map(response => response['Data']));
    }

    getAttendanceCount(pParameters: any): Observable<any> {
        return this.postData(Endpoints.dashboard_attendance_count, pParameters).pipe(map(response => response['Data']['Count']));
    }

    getDashboardMarketWiseOrderVsDelivery(pParameters: any, pUrl: string): Observable<any> {
        return this.getDataForMicroService(pUrl + Endpoints.dashboard_market_wise_order_vs_delivery, pParameters).pipe(map(response => response['data']['content']));
    }

    getDashboardBrandWiseOrderVsDelivery(pParameters: any, pUrl: string): Observable<any> {
        return this.getDataForMicroService(pUrl + Endpoints.dashboard_brand_wise_order_vs_delivery, pParameters).pipe(map(response => response['data']['content']));
    }

    getDashboardMarketWiseTargetDeliveryTotal(pParameters: any, pUrl: string): Observable<any> {
        return this.postDataForMicroService(pUrl + Endpoints.dashboard_market_wise_target_delivery_total, pParameters).pipe(map(response => response['data']));
    }

    getDashboardMarketWiseTargetDeliveryList(pParameters: any, pUrl: string): Observable<any> {
        return this.postDataForMicroService(pUrl + Endpoints.dashboard_market_wise_target_delivery_list, pParameters).pipe(map(response => response['data']));
    }

    getDashboardBrandWiseTargetDeliveryTotal(pParameters: any, pUrl: string): Observable<any> {
        return this.postDataForMicroService(pUrl + Endpoints.dashboard_brand_wise_target_delivery_total, pParameters).pipe(map(response => response['data']));
    }

    getDashboardBrandWiseTargetDeliveryList(pParameters: any, pUrl: string): Observable<any> {
        return this.postDataForMicroService(pUrl + Endpoints.dashboard_brand_wise_target_delivery_list, pParameters).pipe(map(response => response['data']));
    }

    // attendance
    getAttendanceList(pParameters: any): Observable<Attendance[]> {
        return this.postData(Endpoints.attendance_list, pParameters).pipe(map(response => response['Data']['content']));
    }

    saveAttendance(pAttendance: Attendance): Observable<any> {
        return this.postData(pAttendance.Id ? Endpoints.attendance_edit : Endpoints.attendance_add, pAttendance);
    }

    saveAttendanceSubmission(pAttendanceSubmission: Attendance): Observable<any> {
        return this.postData(pAttendanceSubmission.Id ? Endpoints.attendance_submission_update : Endpoints.attendance_submission_add, pAttendanceSubmission);
    }

    getDailyShiftList(pParameters: any): Observable<DailyShift[]> {
        return this.postData(Endpoints.daily_shift_list, pParameters).pipe(map(response => response['Data']['content']));
    }

    getDailyShiftDetails(pDailyShiftId: number): Observable<DailyShift> {
        return this.getData(`${Endpoints.daily_shift_details}/${pDailyShiftId}`).pipe(map(response => response['Data']['DailyShift']));
    }

    saveDailyShift(pDailyShift: DailyShift): Observable<any> {
        return this.postData(pDailyShift.Id ? Endpoints.daily_shift_edit : Endpoints.daily_shift_add, pDailyShift);
    }

    deleteAttendanceSubmissionRule(pDeviceId: number): Observable<any> {
        return this.deleteData(`${Endpoints.attendance_submission_remove}/${pDeviceId}`);
    }

    deleteAttendanceLocationRule(pRuleId: number, pUrl: string): Observable<any> {
        return this.deleteDataForMicroservice(`${pUrl}/${Endpoints.attendance_location_rule_remove}/${pRuleId}`);
    }

    updateAttendanceLocationRule(pParameters: any, pURL: string): Observable<any> {
        return this.postDataForMicroService(pURL + Endpoints.attendance_location_rule_update, pParameters).pipe(map(response => response['data']));
    }

    approveMovement(pApproval: MovementApprove, pURL: string): Observable<any> {
        return this.postDataForMicroService(pURL + Endpoints.movement_approve, pApproval).pipe(map(response => response));
    }

    // campaign
    getProductCampaignList(pParameters: any): Observable<ProductCampaign[]> {
        return this.postData(Endpoints.product_campaign_list, pParameters).pipe(map(response => response['Data']['content']));
    }

    getProductCampaignDetails(pCampaignId: number): Observable<ProductCampaign> {
        return this.getData(`${Endpoints.product_campaign_details}/${pCampaignId}`).pipe(map(response => response['Data']['ProductCampaign']));
    }

    saveProductCampaign(pCampaign: ProductCampaign): Observable<any> {
        return this.postData(pCampaign.Id ? Endpoints.product_campaign_edit : Endpoints.product_campaign_add, pCampaign);
    }

    getDealerCampaignDetails(pDealerCampaignId: number): Observable<DealerCampaign> {
        return this.getData(`${Endpoints.dealer_campaign_details}/${pDealerCampaignId}`).pipe(map(response => response['Data']['Campaign']));
    }

    getCustomerCampaignDetails(pCustomerCampaignId: number): Observable<CustomerCampaign> {
        return this.getData(`${Endpoints.customer_camp_details}/${pCustomerCampaignId}`).pipe(map(response => response['Data']['Campaign']));
    }

    getRetailerToDealerCampaignDetails(pRetailerToDealerCampaignId: number): Observable<RetailerToDealerCampaign> {
        return this.getData(`${Endpoints.retailer_to_dealer_campaign_details}/${pRetailerToDealerCampaignId}`).pipe(map(response => response['Data']['Campaign']));
    }

    getDistributorCampaignDetails(pCustomerCampaignId: number): Observable<DistributorCampaign> {
        return this.getData(`${Endpoints.distributor_camp_details}/${pCustomerCampaignId}`).pipe(map(response => response['Data']['Campaign']));
    }

    saveCustomerCampaign(pCustomerCampaign: CustomerCampaign): Observable<CustomerCampaign> {
        return this.postData(pCustomerCampaign.Id ? Endpoints.customer_camp_edit : Endpoints.customer_camp_add, pCustomerCampaign);
    }

    saveRetailerToDealerCampaign(pRetailerToDealerCampaign: RetailerToDealerCampaign): Observable<RetailerToDealerCampaign> {
        return this.postData(pRetailerToDealerCampaign.Id ? Endpoints.retailer_to_dealer_campaign_edit : Endpoints.retailer_to_dealer_campaign_add, pRetailerToDealerCampaign);
    }

    saveDealerCampaign(pDealerCampaign: DealerCampaign): Observable<DealerCampaign> {
        return this.postData(pDealerCampaign.Id ? Endpoints.dealer_campaign_edit : Endpoints.dealer_campaign_add, pDealerCampaign);
    }

    saveDistributorCampaign(pCustomerCampaign: DistributorCampaign): Observable<DistributorCampaign> {
        return this.postData(pCustomerCampaign.Id ? Endpoints.distributor_camp_edit : Endpoints.distributor_camp_add, pCustomerCampaign);
    }


    // central stock
    getTransferFromCentralStockDetails(pTransferId: number): Observable<TransferFromCentralStock> {
        return this.getData(`${Endpoints.central_stock_transfer_details}/${pTransferId}`).pipe(map(response => response['Data']['Id']));
    }

    approveCentralStockTransfer(pApproval: Approval): Observable<any> {
        return this.postData(Endpoints.central_stock_transfer_approve, pApproval);
    }

    saveProductToCentralStock(pCentralStock: CentralStock): Observable<any> {
        return this.postData(Endpoints.central_stock_add, pCentralStock);
    }


    // code generator rule
    saveCodeGeneratorRule(pCode: CodeGeneratorRule): Observable<any> {
        return this.postData(pCode.Id ? Endpoints.code_generator_rule_edit : Endpoints.code_generator_rule_add, pCode);
    }

    getCodeGeneratorRuleDetails(pId: number): Observable<CodeGeneratorRule> {
        return this.getData(`${Endpoints.code_generator_rule_detail}/${pId}`).pipe(map(response => response['Data']['CodeGeneratorRule']));
    }

    deleteCodeGeneratorRule(pId: number): Observable<any> {
        return this.deleteData(`${Endpoints.code_generator_rule_delete}/${pId}`);
    }


    // ta da
    getDailyTaDaClaimDetails(pTaDaClaimId: number): Observable<DailyTaDaClaim> {
        return this.getData(`${Endpoints.daily_ta_da_claim_details}/${pTaDaClaimId}`).pipe(map(response => response['Data']['DailyTaDaClaim']));
    }

    getDailyTaDaList(pParameters: any): Observable<DailyTaDa[]> {
        return this.postData(Endpoints.daily_ta_da_list, pParameters).pipe(map(response => response['Data']['content']));
    }

    getDailyTaDaDetails(pTaDaId: number): Observable<DailyTaDa> {
        return this.getData(`${Endpoints.daily_ta_da_details}/${pTaDaId}`).pipe(map(response => response['Data']['DailyTaDa']));
    }

    getDailyTaDaDetailsByUserRoleMarket(pUserRole: UserRole, pMarket: Market): Observable<DailyTaDa> {
        return this.postData(Endpoints.daily_ta_da_details_by_user_role_market, {
            UserRole: pUserRole,
            Market: pMarket
        }).pipe(map(response => response ? response['Data']['DailyTaDa'] : null));
    }

    saveDailyTaDa(pDailyTaDa: DailyTaDa): Observable<any> {
        return this.postData(Endpoints.daily_ta_da_manage, pDailyTaDa);
    }


    getDailyTaDaClaimList(pParameters: any): Observable<DailyTaDaClaim[]> {
        return this.postData(Endpoints.daily_ta_da_claim_list, pParameters).pipe(map(response => response['Data']['content']));
    }

    getDailyTaDaClaimDetailsByUserMarketDate(pUserId: number, pMarketId: number, pClaimDate: string): Observable<DailyTaDaClaim> {
        return this.postData(Endpoints.daily_ta_da_claim_details_by_user_market_date, {
            User: new User(pUserId),
            Market: new Market(pMarketId),
            ClaimDate: pClaimDate
        }).pipe(map(response => response['Data']['DailyTaDaClaim']));
    }

    saveDailyTaDaClaim(pDailyTaDaClaim: DailyTaDaClaim): Observable<any> {
        return this.postData(pDailyTaDaClaim.Id ? Endpoints.daily_ta_da_claim_edit : Endpoints.daily_ta_da_claim_add, pDailyTaDaClaim);
    }

    resetDailyTaDaClaim(pDailyTaDaClaimId: Approval): Observable<any> {
        return this.postData(Endpoints.daily_ta_da_claim_reset, pDailyTaDaClaimId);
    }

    deleteDailyTaDaClaim(pClaimId: number): Observable<any> {
        return this.deleteData(`${Endpoints.daily_ta_da_claim_delete}/${pClaimId}`);
    }

    approveTourAllowanceClaim(pApproval: Approval): Observable<any> {
        return this.postData(Endpoints.tour_allowance_claim_approve, pApproval);
    }

    addAllowanceRule(pTourAllowanceRule: TourAllowanceRule): Observable<any> {
        return this.postData(pTourAllowanceRule.Id ? Endpoints.edit_allowance_rule : Endpoints.create_allowance_rule, pTourAllowanceRule);
    }

    deleteAllowanceRule(pAllowanceRuleId: number): Observable<any> {
        return this.deleteData(`${Endpoints.delete_allowance_rule}/${pAllowanceRuleId}`);
    }

    resetTAClaim(pParameters: any, pURL: string): Observable<TaClaim> {
        return this.postDataForMicroService(pURL + Endpoints.ta_claim_request_reset, pParameters).pipe(map(response => response['data']['content']));
    }

    getTAClaimDetails(pId: number, pURL: string): Observable<TaClaim> {
        return this.getDataForMicroService(pURL + `${Endpoints.ta_claim_request_details}/${pId}`, {}).pipe(map(response => response['data']));
    }

    saveTAClaim(pData: any, pMicroServiceUrl: string): Observable<TaClaim> {
        return this.postDataForMicroService(pMicroServiceUrl + Endpoints.ta_claim_request_approve, pData).pipe(map(response => response['data']['content']));
    }

    applyTaForSubordinates(pData: any, pURL: string): Observable<TaForSubordinates> {
        return this.postDataForMicroService(pURL + Endpoints.apply_ta_for_subordinates, pData).pipe(map(response => response['data']['content']));
    }

    triggerDaProcessing(pData: TriggerDa, pURL: string): Observable<any> {
        return this.postDataForMicroService(pURL + Endpoints.trigger_da, pData).pipe(map(response => response['data']['message']));
    }

    editAppliedDa(pData: AppliedDa, pURL: string): Observable<any> {
        return this.postDataForMicroService(pURL + Endpoints.edit_applied_da, pData).pipe(map(response => response['data']));
    }

    // dealer

    getDealerListByMarket(pParameters: any): Observable<Dealer[]> {
        return this.postData(Endpoints.dealer_list_by_market, pParameters).pipe(map(response => response['Data']['content']));
    }

    getDealerList(pParameters: any): Observable<Dealer[]> {
        return this.postData(Endpoints.dealer_list, pParameters).pipe(map(response => response['Data']['content']));
    }

    saveDealer(pDealer: Dealer): Observable<any> {
        return this.postData(pDealer.Id ? Endpoints.dealer_edit : Endpoints.dealer_add, pDealer);
    }

    deleteDealer(pDealerId: number): Observable<any> {
        return this.deleteData(`${Endpoints.dealer_delete}/${pDealerId}`);
    }

    getDealerTypeList(pParameters: any): Observable<DealerType[]> {
        return this.postData(Endpoints.dealer_type_list, pParameters).pipe(map(response => response['Data']['content']));
    }

    getDealerDetails(pDealerId: number): Observable<Dealer> {
        return this.getData(`${Endpoints.dealer_details}/${pDealerId}`).pipe(map(response => response['Data']['Dealer']));
    }

    saveDealerType(pDealerType: DealerType): Observable<any> {
        return this.postData(pDealerType.Id ? Endpoints.dealer_type_edit : Endpoints.dealer_type_add, pDealerType);
    }

    getDealerPaymentDetails(pDealerPaymentId: number): Observable<DealerPayment> {
        return this.getData(`${Endpoints.dealer_payment_details}/${pDealerPaymentId}`).pipe(map(response => response['Data']['DealerPayment']));
    }


    // delivery challan
    getDistributorDeliveryChallanList(pParameters: any): Observable<DistributorDeliveryChallan[]> {
        return this.postData(Endpoints.distributor_delivery_challan_list, pParameters).pipe(map(response => response['Data']['content']));
    }

    getDistributorDeliveryChallanDetails(pDistributorDeliveryChallanId: number): Observable<DistributorDeliveryChallan> {
        return this.getData(`${Endpoints.distributor_delivery_challan_details}/${pDistributorDeliveryChallanId}`)
            .pipe(map(response => response['Data']['DistributorDeliveryChallan']));
    }

    getDistributorDeliveryChallanDetailsByProduct(pDistributorDeliveryChallanId: number): Observable<DistributorDeliveryChallan> {
        return this.getData(`${Endpoints.distributor_delivery_challan_by_product_details}/${pDistributorDeliveryChallanId}`)
            .pipe(map(response => response['Data']['DistributorDeliveryChallan']));
    }


    getDistributorDeliveryChallanAdvice(pParameters: any): Observable<DistributorDeliveryChallan> {
        return this.postData(Endpoints.distributor_delivery_challan_advice, pParameters).pipe(map(response => response['Data']['DistributorDeliveryChallan']));
    }

    saveDistributorDeliveryChallan(pDistributorDeliveryChallan: DistributorDeliveryChallan): Observable<any> {
        return this.postData(Endpoints.distributor_delivery_challan_add, pDistributorDeliveryChallan);
    }

    cancelDistributorDeliveryChallan(pChallanId: number): Observable<any> {
        return this.getData(`${Endpoints.distributor_delivery_challan_cancel}/${pChallanId}`);
    }


    cancelDistributorDeliveryChallanByProductSerial(pChallanId: number): Observable<any> {
        return this.getData(`${Endpoints.distributor_delivery_challan_cancel_by_product_serial}/${pChallanId}`);
    }

    approveDistributorDeliveryChallan(pApproval: Approval): Observable<any> {
        return this.postData(Endpoints.distributor_delivery_challan_approve, pApproval);
    }

    approveDistributorDeliveryChallanBySerial(pApproval: Approval): Observable<any> {
        return this.postData(Endpoints.distributor_delivery_challan_approve_by_serial, pApproval);
    }

    getCustomerDeliveryChallanList(pParameters: any): Observable<CustomerDeliveryChallan[]> {
        return this.postData(Endpoints.customer_delivery_challan_list, pParameters).pipe(map(response => response['Data']['content']));
    }

    getCustomerDeliveryChallanDetails(pCustomerDeliveryChallanId: number): Observable<CustomerDeliveryChallan> {
        return this.getData(`${Endpoints.customer_delivery_challan_details}/${pCustomerDeliveryChallanId}`)
            .pipe(map(response => response['Data']['CustomerDeliveryChallan']));
    }

    getDealerDeliveryChallanDetails(pDealerDeliveryChallanId: number): Observable<DistributorDealerDelivery> {
        return this.getData(`${Endpoints.dealer_delivery_challan_details}/${pDealerDeliveryChallanId}`)
            .pipe(map(response => response['Data']['DealerDeliveryChallan']));
    }

    getDealerDeliveryChallanAdvice(pParameters: any): Observable<DistributorDealerDelivery> {
        return this.postData(Endpoints.dealer_delivery_challan_advice, pParameters).pipe(map(response => response['Data']['DealerDeliveryChallan']));
    }

    saveDealerDeliveryChallan(pDealerDeliveryChallan: DistributorDealerDelivery): Observable<any> {
        return this.postData(Endpoints.dealer_delivery_challan_add, pDealerDeliveryChallan);
    }

    cancelDealerDeliveryChallan(pChallanId: number): Observable<any> {
        return this.getData(`${Endpoints.dealer_delivery_challan_cancel}/${pChallanId}`);
    }

    approveDealerDeliveryChallan(pApproval: Approval): Observable<any> {
        return this.postData(Endpoints.dealer_delivery_challan_approve, pApproval);
    }

    getRetailerToDealerDeliveryChallanDetails(pDealerDeliveryChallanId: number): Observable<RetailerToDealerDelivery> {
        return this.getData(`${Endpoints.retailer_to_dealer_delivery_challan_details}/${pDealerDeliveryChallanId}`)
            .pipe(map(response => response['Data']['RetailerToDealerDeliveryChallan']));
    }

    getRetailerToDealerDeliveryChallanAdvice(pParameters: any): Observable<RetailerToDealerDelivery> {
        return this.postData(Endpoints.retailer_to_dealer_delivery_challan_advice, pParameters).pipe(map(response => response['Data']['RetailerToDealerDeliveryChallan']));
    }

    saveRetailerToDealerDeliveryChallan(pDealerDeliveryChallan: RetailerToDealerDelivery): Observable<any> {
        return this.postData(Endpoints.retailer_to_dealer_delivery_challan_add, pDealerDeliveryChallan);
    }

    cancelRetailerToDealerDeliveryChallan(pChallanId: number): Observable<any> {
        return this.getData(`${Endpoints.retailer_to_dealer_delivery_challan_cancel}/${pChallanId}`);
    }

    approveRetailerToDealerDeliveryChallan(pApproval: Approval): Observable<any> {
        return this.postData(Endpoints.retailer_to_dealer_delivery_challan_approve, pApproval);
    }

    getCustomerDeliveryChallanDetailsByProduct(pCustomerDeliveryChallanId: number): Observable<CustomerDeliveryChallanByProduct> {
        return this.getData(`${Endpoints.customer_delivery_challan_details_by_product}/${pCustomerDeliveryChallanId}`)
            .pipe(map(response => response['Data']['CustomerDeliveryChallan']));
    }

    getCustomerDeliveryChallanAdvice(pParameters: any): Observable<CustomerDeliveryChallan> {
        return this.postData(Endpoints.customer_delivery_challan_advice, pParameters).pipe(map(response => response['Data']['CustomerDeliveryChallan']));
    }

    getCustomerDeliveryChallanAdviceByProduct(pParameters: any): Observable<CustomerDeliveryChallanByProduct> {
        return this.postData(Endpoints.customer_delivery_challan_advice_by_product, pParameters).pipe(map(response => response['Data']['CustomerDeliveryChallan']));
    }

    saveCustomerDeliveryChallan(pCustomerDeliveryChallan: CustomerDeliveryChallan): Observable<any> {
        return this.postData(Endpoints.customer_delivery_challan_add, pCustomerDeliveryChallan);
    }

    saveCustomerDeliveryChallanByProduct(pCustomerDeliveryChallan: CustomerDeliveryChallanByProduct): Observable<any> {
        return this.postData(Endpoints.customer_delivery_challan_add_by_product, pCustomerDeliveryChallan);
    }

    cancelCustomerDeliveryChallan(pChallanId: number): Observable<any> {
        return this.getData(`${Endpoints.customer_delivery_challan_cancel}/${pChallanId}`);
    }

    cancelCustomerDeliveryChallanByProduct(pChallanId: number): Observable<any> {
        return this.getData(`${Endpoints.customer_delivery_challan_cancel_by_product}/${pChallanId}`);
    }

    approveCustomerDeliveryChallan(pApproval: Approval): Observable<any> {
        return this.postData(Endpoints.customer_delivery_challan_approve, pApproval);
    }

    approveCustomerDeliveryChallanByProduct(pApproval: Approval): Observable<any> {
        return this.postData(Endpoints.customer_delivery_challan_approve_by_product, pApproval);
    }

    getCustomerListForDeliveryChallan(pParameters: any): Observable<Customer[]> {
        return this.postData(Endpoints.customer_list_for_delivery_challan, pParameters).pipe(map(response => response['Data']['content']));
    }


    // distribution center
    getDistributorCentreList(pParameters: any): Observable<DistributorCenter[]> {
        return this.postData(Endpoints.distributor_centre_list, pParameters).pipe(map(response => response['Data']['content']));
    }

    getDistributorCentreDetails(pDistributorCentreId: number): Observable<DistributorCenter> {
        return this.getData(`${Endpoints.distributor_centre_details}/${pDistributorCentreId}`).pipe(map(response => response['Data']['DistributionCenter']));
    }

    saveDistributorCentre(pDistributorCentre: DistributorCenter): Observable<any> {
        return this.postData(pDistributorCentre.Id ? Endpoints.distributor_centre_edit : Endpoints.distributor_centre_add, pDistributorCentre);
    }


    // distributor
    getDistributorTypeList(pParameters: any): Observable<DistributorType[]> {
        return this.postData(Endpoints.distributor_type_list, pParameters).pipe(map(response => response['Data']['content']));
    }

    getDistributorTypeDetails(pDistributorTypeId: number): Observable<DistributorType> {
        return this.getData(`${Endpoints.distributor_type_details}/${pDistributorTypeId}`).pipe(map(response => response['Data']['DistributorType']));
    }

    saveDistributorType(pDistributorType: DistributorType): Observable<any> {
        return this.postData(pDistributorType.Id ? Endpoints.distributor_type_edit : Endpoints.distributor_type_add, pDistributorType);
    }

    getDistributorList(pParameters: any): Observable<Distributor[]> {
        return this.postData(Endpoints.distributor_list, pParameters).pipe(map(response => response['Data']['content']));
    }

    getDistributorListByMarket(pParameters: any): Observable<Distributor[]> {
        return this.postData(Endpoints.distributor_list_by_market, pParameters).pipe(map(response => response['Data']['content']));
    }

    getDistributorDetails(pDistributorId: number): Observable<Distributor> {
        return this.getData(`${Endpoints.distributor_details}/${pDistributorId}`).pipe(map(response => response['Data']['Distributor']));
    }

    saveDistributor(pDistributor: Distributor): Observable<any> {
        return this.postData(pDistributor.Id ? Endpoints.distributor_edit : Endpoints.distributor_add, pDistributor);
    }

    getDistributorRequestList(pParameters: any): Observable<Distributor[]> {
        return this.postData(Endpoints.distributor_request_list, pParameters).pipe(map(response => response['Data']['content']));
    }

    getSimilarDistributorList(pParameters: any): Observable<Distributor[]> {
        return this.postData(Endpoints.distributor_request_sound_alike, pParameters).pipe(map(response => response['Data']['content']));
    }

    getDistributorRequestDetails(pDistributorRequestId: number): Observable<Distributor> {
        return this.getData(`${Endpoints.distributor_request_details}/${pDistributorRequestId}`).pipe(map(response => response['Data']['Distributor']));
    }

    editDistributorRequest(pDistributor: Distributor): Observable<any> {
        return this.postData(Endpoints.distributor_request_edit, pDistributor);
    }

    getDistributorUserDetails(pUserId: number): Observable<User> {
        return this.getData(`${Endpoints.distributor_user_details}/${pUserId}`).pipe(map(response => response['Data']['User']));
    }

    getDistributorListForDeliveryChallan(pParameters: any): Observable<Distributor[]> {
        return this.postData(Endpoints.distributor_list_for_delivery_challan, pParameters).pipe(map(response => response['Data']['content']));
    }

    getUserListOfDistributor(pParameters: any): Observable<User[]> {
        return this.postData(Endpoints.user_list_of_distributor, pParameters).pipe(map(response => response['Data']['content']));
    }

    getDistributorMarketList(pParameters: any): Observable<Market[]> {
        return this.postData(Endpoints.market_list_of_distributor, pParameters).pipe(map(response => response['Data']['content']));
    }

    getRetailerListOfDistributor(pParameters: any): Observable<Customer[]> {
        return this.postData(Endpoints.customer_list_of_distributor, pParameters).pipe(map(response => response['Data']['content']));
    }

    getDistributorBankAccountList(pParameters: any): Observable<DistributorBankAccount[]> {
        return this.postData(Endpoints.distributor_bank_account_list, pParameters).pipe(map(response => response['Data']['content']));
    }

    getDistributorBankAccountDetails(pDistributorBankAccountId: number): Observable<DistributorBankAccount> {
        return this.getData(`${Endpoints.distributor_bank_account_details}/${pDistributorBankAccountId}`)
            .pipe(map(response => response['Data']['DistributorBankAccount']));
    }

    saveDistributorBankAccount(pDistributorBankAccount: DistributorBankAccount): Observable<any> {
        return this.postData(pDistributorBankAccount.Id ? Endpoints.distributor_bank_account_edit : Endpoints.distributor_bank_account_add, pDistributorBankAccount);
    }

    approveDistributorBankAccount(pApproval: Approval): Observable<any> {
        return this.postData(Endpoints.distributor_bank_account_approve, pApproval);
    }

    deleteDistributorBankAccount(pDistributorBankAccountId: number): Observable<any> {
        return this.deleteData(`${Endpoints.distributor_bank_account_delete}/${pDistributorBankAccountId}`);
    }


    getDistributorPaymentList(pParameters: any): Observable<DistributorPayment[]> {
        return this.postData(Endpoints.distributor_payment_list, pParameters).pipe(map(response => response['Data']['content']));
    }

    getDistributorPaymentDetails(pDistributorPaymentId: number): Observable<DistributorPayment> {
        return this.getData(`${Endpoints.distributor_payment_details}/${pDistributorPaymentId}`).pipe(map(response => response['Data']['DistributorPayment']));
    }

    getSafetyStockDetails(pSafetyStockId: number): Observable<SafetyStock> {
        return this.getData(`${Endpoints.distributor_safety_stock_detail}/${pSafetyStockId}`).pipe(map(response => response['Data']['DistributorSafetyStock']));
    }

    saveDistributorPayment(pDistributorPayment: DistributorPayment): Observable<any> {
        return this.postData(pDistributorPayment.Id ? Endpoints.distributor_payment_edit : Endpoints.distributor_payment_add, pDistributorPayment);
    }

    approveDepositAccount(pApproval: any): Observable<any> {
        return this.postData(Endpoints.distributor_payment_with_deposit_approve, pApproval);
    }

    resetDistributorPayment(pId: number): Observable<any> {
        return this.getData(`${Endpoints.distributor_payment_reset}/${pId}`).pipe(map(response => response['Data']['Success']));
    }

    cancelDistributorPayment(pId: number): Observable<any> {
        return this.getData(`${Endpoints.distributor_payment_cancel}/${pId}`).pipe(map(response => response['Data']['Success']));
    }

    saveSafetyStock(pSafetyStock: SafetyStock): Observable<any> {
        return this.postData(pSafetyStock.Id ? Endpoints.distributor_safety_stock_edit : Endpoints.distributor_safety_stock_add, pSafetyStock);
    }

    approveDistributorPayment(pApproval: Approval): Observable<any> {
        return this.postData(Endpoints.distributor_payment_approve, pApproval);
    }

    getDistributorInvoiceList(pParameters: any): Observable<DistributorInvoice[]> {
        return this.postData(Endpoints.distributor_invoice_list, pParameters).pipe(map(response => response['Data']['content']));
    }

    getDistributorInvoiceDetails(pInvoiceId: number): Observable<DistributorInvoice> {
        return this.getData(`${Endpoints.distributor_invoice_details}/${pInvoiceId}`).pipe(map(response => response['Data']['DistributorInvoice']));
    }

    saveDistributorInvoice(pInvoice: DistributorInvoice): Observable<any> {
        return this.postData(pInvoice.Id ? Endpoints.distributor_invoice_edit : Endpoints.distributor_invoice_add, pInvoice);
    }

    getDistributorInvoiceCampaignPreview(pInvoice: DistributorInvoice): Observable<DistributorInvoice> {
        return this.postData(Endpoints.distributor_invoice_campaign_preview, pInvoice).pipe(map(response => response['Data']['DistributorInvoice']));
    }

    cancelDistributorInvoice(pInvoiceId: number): Observable<any> {
        return this.postData(Endpoints.distributor_invoice_cancel, new Approval(pInvoiceId, false));
    }

    saveDelivaryChallanByProduct(pProduct: DistributorDeliveryChallanByProduct): Observable<any> {
        return this.postData(Endpoints.distributor_delivery_challan_add_by_product, pProduct);
    }


    // expense
    getExpenseTypeList(pParameters: any): Observable<ExpenseType[]> {
        return this.postData(Endpoints.expense_type_list, pParameters).pipe(map(response => response['Data']['content']));
    }

    getExpenseSubTypeList(pParameters: any): Observable<ExpenseSubType[]> {
        return this.postData(Endpoints.expense_sub_type_list, pParameters).pipe(map(response => response['Data']['content']));
    }

    getExpenseTypeDetails(pExpenseTypeId: number): Observable<ExpenseType> {
        return this.getData(`${Endpoints.expense_type_details}/${pExpenseTypeId}`).pipe(map(response => response['Data']['ExpenseType']));
    }

    getExpenseSubTypeDetails(pExpenseSubTypeId: number): Observable<ExpenseSubType> {
        return this.getData(`${Endpoints.expense_sub_type_details}/${pExpenseSubTypeId}`).pipe(map(response => response['Data']['ExpenseSubType']));
    }

    saveExpenseType(pExpenseType: ExpenseType): Observable<any> {
        return this.postData(pExpenseType.Id ? Endpoints.expense_type_edit : Endpoints.expense_type_add, pExpenseType);
    }

    saveExpenseSubType(pExpenseSubType: ExpenseSubType): Observable<any> {
        return this.postData(pExpenseSubType.Id ? Endpoints.expense_sub_type_edit : Endpoints.expense_sub_type_add, pExpenseSubType);
    }

    getExpensePurposeList(pParameters: any): Observable<ExpensePurpose[]> {
        return this.postData(Endpoints.expense_purpose_list, pParameters).pipe(map(response => response['Data']['content']));
    }

    getExpensePurposeDetails(pExpensePurposeId: number): Observable<ExpensePurpose> {
        return this.getData(`${Endpoints.expense_purpose_details}/${pExpensePurposeId}`).pipe(map(response => response['Data']['ExpensePurpose']));
    }

    saveExpensePurpose(pExpensePurpose: ExpensePurpose): Observable<any> {
        return this.postData(pExpensePurpose.Id ? Endpoints.expense_purpose_edit : Endpoints.expense_purpose_add, pExpensePurpose);
    }

    getExpenseClaimList(pParameters: any): Observable<ExpenseClaim[]> {
        return this.postData(Endpoints.expense_claim_list, pParameters).pipe(map(response => response['Data']['content']));
    }

    getExpenseClaimDetails(pExpenseClaimId: number): Observable<ExpenseClaim> {
        return this.getData(`${Endpoints.expense_claim_details}/${pExpenseClaimId}`).pipe(map(response => response['Data']['ExpenseClaim']));
    }

    getExpenseClaimDetailsByUserMarketExpenseDate(pUserId: number, pMarketId: number, pExpenseDate): Observable<ExpenseClaim> {
        return this.postData(Endpoints.expense_claim_list, {
            UserId: pUserId,
            MarketIdList: [pMarketId],
            Only: true,
            FromDate: pExpenseDate,
            ToDate: pExpenseDate
        }).pipe(map(response => response['Data']['content'] ? response['Data']['content'][0] : null));
    }

    saveExpenseClaim(pExpenseClaim: ExpenseClaim): Observable<any> {
        return this.postData(pExpenseClaim.Id ? Endpoints.expense_claim_edit : Endpoints.expense_claim_add, pExpenseClaim);
    }

    getBillingOrganizationList(pParameters: any): Observable<BillingOrganization[]> {
        return this.postData(Endpoints.billing_organization_list, pParameters).pipe(map(response => response['Data']['content']));
    }

    getBilling_OrganizationList(pParameters: any): Observable<BillingOrganization[]> {
        return this.postData(Endpoints.billingOrganization_list, pParameters).pipe(map(response => response['Data']['content']));
    }


    // geo hierarchy
    getDivisionList(pParameters: any): Observable<Division[]> {
        return this.postData(Endpoints.division_list, pParameters).pipe(map(response => response['Data']['content']));
    }

    getDistrictList(pParameters: any): Observable<District[]> {
        return this.postData(Endpoints.district_list, pParameters).pipe(map(response => response['Data']['content']));
    }

    getThanaList(pParameters: any): Observable<Thana[]> {
        return this.postData(Endpoints.thana_list, pParameters).pipe(map(response => response['Data']['content']));
    }

    getUnionOrTownList(pParameters: any): Observable<UnionOrTown[]> {
        return this.postData(Endpoints.union_or_town_list, pParameters).pipe(map(response => response['Data']['content']));
    }

    saveDivision(pDivision: Division): Observable<any> {
        return this.postData(pDivision.Id ? Endpoints.division_edit : Endpoints.division_add, pDivision);
    }

    saveDistrict(pDistrict: District): Observable<any> {
        return this.postData(pDistrict.Id ? Endpoints.district_edit : Endpoints.district_add, pDistrict);
    }

    saveThana(pThana: Thana): Observable<any> {
        return this.postData(pThana.Id ? Endpoints.thana_edit : Endpoints.thana_add, pThana);
    }

    saveUnionOrTown(pUnionOrTown: UnionOrTown): Observable<any> {
        return this.postData(pUnionOrTown.Id ? Endpoints.union_or_town_edit : Endpoints.union_or_town_add, pUnionOrTown);
    }


    // invoice

    getCustomerInvoiceList(pParameters: any): Observable<CustomerInvoice[]> {
        return this.postData(Endpoints.customer_invoice_list, pParameters).pipe(map(response => response['Data']['content']));
    }

    getCustomerInvoiceDetails(pInvoiceId: number): Observable<CustomerInvoice> {
        return this.getData(`${Endpoints.customer_invoice_details}/${pInvoiceId}`).pipe(map(response => response['Data']['CustomerInvoice']));
    }

    getRetailerInvoiceTotalAmount(pParameters: any): Observable<any> {
        return this.postData(Endpoints.customer_invoice_total_amount, pParameters).pipe(map(response => response['Data']));
    }

    saveCustomerInvoice(pInvoice: CustomerInvoice): Observable<any> {
        return this.postData(pInvoice.Id ? Endpoints.customer_invoice_edit : Endpoints.customer_invoice_add, pInvoice);
    }

    createMultipleCustomerInvoice(pOrderId: number): Observable<SecondaryOrder> {
        return this.getData(`${Endpoints.multiple_invoice_from_order}/${pOrderId}`, pOrderId);
    }

    createMultipleRetailerToDealerInvoice(pOrderId: number): Observable<RetailerToDealerOrder> {
        return this.getData(`${Endpoints.retailer_to_dealer_invoice_from_order}/${pOrderId}`, pOrderId);
    }

    getDealerInvoiceDetails(pInvoiceId: number): Observable<DistributorDealerInvoice> {
        return this.getData(`${Endpoints.dealer_invoice_details}/${pInvoiceId}`).pipe(map(response => response['Data']['DealerInvoice']));
    }

    getRetailerToDealerInvoiceDetails(pInvoiceId: number): Observable<RetailerToDealerInvoice> {
        return this.getData(`${Endpoints.retailer_to_dealer_invoice_details}/${pInvoiceId}`).pipe(map(response => response['Data']['RetailerToDealerInvoice']));
    }

    saveDealerInvoice(pInvoice: DistributorDealerInvoice): Observable<any> {
        return this.postData(pInvoice.Id ? Endpoints.dealer_invoice_edit : Endpoints.dealer_invoice_add, pInvoice);
    }

    saveRetailerToDealerInvoice(pInvoice: RetailerToDealerInvoice): Observable<any> {
        return this.postData(pInvoice.Id ? Endpoints.retailer_to_dealer_invoice_edit : Endpoints.retailer_to_dealer_invoice_add, pInvoice);
    }

    getDealerInvoiceList(pParameters: any): Observable<DistributorDealerInvoice[]> {
        return this.postData(Endpoints.dealer_invoice_list, pParameters).pipe(map(response => response['Data']['content']));
    }

    getRetailerToDealerInvoiceList(pParameters: any): Observable<RetailerToDealerInvoice[]> {
        return this.postData(Endpoints.retailer_to_dealer_invoice_list, pParameters).pipe(map(response => response['Data']['content']));
    }

    getCustomerInvoiceCampaignPreview(pInvoice: CustomerInvoice): Observable<CustomerInvoice> {
        return this.postData(Endpoints.customer_invoice_campaign_preview, pInvoice).pipe(map(response => response['Data']['CustomerInvoice']));
    }

    cancelCustomerInvoice(pInvoiceId: number): Observable<any> {
        return this.postData(Endpoints.customer_invoice_cancel, new Approval(pInvoiceId, false));
    }

    cancelDealerInvoice(pInvoiceId: number): Observable<any> {
        return this.postData(Endpoints.dealer_invoice_cancel, new Approval(pInvoiceId, false));
    }

    cancelRetailerToDealerInvoice(pInvoiceId: number): Observable<any> {
        return this.postData(Endpoints.retailer_to_dealer_invoice_cancel, new Approval(pInvoiceId, false));
    }

    getDistributorInvoicePreview(pOrder: DistributorInvoice): Observable<DistributorInvoice> {
        return this.postData(Endpoints.distributor_invoice_preview, pOrder).pipe(map(response => response['Data']['DistributorInvoice']));
    }

    getCustomerInvoicePreview(pOrder: CustomerInvoice): Observable<CustomerInvoice> {
        return this.postData(Endpoints.customer_invoice_preview, pOrder).pipe(map(response => response['Data']['CustomerInvoice']));
    }

    getDealerInvoicePreview(pOrder: DistributorDealerInvoice): Observable<DistributorDealerInvoice> {
        return this.postData(Endpoints.dealer_invoice_preview, pOrder).pipe(map(response => response['Data']['DealerInvoice']));
    }

    getRetailerToDealerInvoicePreview(pOrder: RetailerToDealerInvoice): Observable<RetailerToDealerInvoice> {
        return this.postData(Endpoints.retailer_to_dealer_invoice_preview, pOrder).pipe(map(response => response['Data']['RetailerToDealerInvoice']));
    }

    getFmcgCustomerInvoicePreview(pCustomerInvoiceRequestDto: CustomerInvoiceRequestDto, pUrl: string): Observable<CustomerInvoicePreview> {
        return this.postDataForMicroService(pUrl + Endpoints.fmcg_customer_invoice_preview, pCustomerInvoiceRequestDto).pipe(map(response => response['data']));
    }

    saveFmcgCustomerInvoice(pCustomerInvoiceRequestDto: CustomerInvoiceRequestDto, pUrl: string): Observable<any> {
        return this.postDataForMicroService(pUrl + Endpoints.save_fmcg_customer_invoice, pCustomerInvoiceRequestDto).pipe(map(response => response['data']));
    }

    getCustomerInvoicePreviewAfterDelivery(pCustomerInvoiceRequestDto: CustomerInvoiceAfterDeliveryRequestDto, pUrl: string): Observable<CustomerInvoicePreview> {
        return this.postDataForMicroService(pUrl + Endpoints.customer_invoice_preview_after_delivery, pCustomerInvoiceRequestDto).pipe(map(response => response['data']));
    }

    saveCustomerInvoiceAfterDelivery(pCustomerInvoiceRequestDto: CustomerInvoiceAfterDeliveryRequestDto, pUrl: string): Observable<any> {
        return this.postDataForMicroService(pUrl + Endpoints.edit_customer_invoice_after_delivery, pCustomerInvoiceRequestDto).pipe(map(response => response['data']));
    }

    getFmcgCustomerInvoiceDetails(pId: number, pURL: string): Observable<CustomerInvoiceDto> {
        return this.getDataForMicroService(pURL + `${Endpoints.fmcg_customer_invoice_details}/${pId}`, {}).pipe(map(response => response['data']));
    }

    deliverFmcgCustomerInvoice(pId: number, pURL: string): Observable<any> {
        return this.postDataForMicroService(pURL + `${Endpoints.fmcg_customer_invoice_deliver}/${pId}`, {}).pipe(map(response => response['data']));
    }

    rejectFmcgCustomerInvoice(pId: number, pURL: string): Observable<any> {
        return this.postDataForMicroService(pURL + `${Endpoints.fmcg_customer_invoice_reject}/${pId}`, {}).pipe(map(response => response['data']));
    }

    cancelFmcgCustomerInvoice(pId: number, pURL: string): Observable<any> {
        return this.postDataForMicroService(pURL + `${Endpoints.customer_invoice_cancel_v2}/${pId}`, {}).pipe(map(response => response['data']));
    }


    // leave management
    getLeaveAccountList(pParameters: any): Observable<LeaveAccount[]> {
        return this.postData(Endpoints.leave_account_list, pParameters).pipe(map(response => response['Data']['content']));
    }

    saveLeaveAccount(pLeaveAccount: LeaveAccount): Observable<any> {
        return this.postData(Endpoints.leave_account_manage, pLeaveAccount);
    }

    getHolidayList(pParameters: any): Observable<Holiday[]> {
        return this.postData(Endpoints.holiday_list, pParameters).pipe(map(response => response['Data']['content']));
    }

    getHolidayDetails(pHolidayId: number): Observable<Holiday> {
        return this.getData(`${Endpoints.holiday_details}/${pHolidayId}`).pipe(map(response => response['Data']['Holiday']));
    }

    saveHoliday(pHoliday: Holiday): Observable<any> {
        return this.postData(pHoliday.Id ? Endpoints.holiday_edit : Endpoints.holiday_add, pHoliday);
    }

    getLeaveTypeList(pParameters: any): Observable<LeaveType[]> {
        return this.postData(Endpoints.leave_type_list, pParameters).pipe(map(response => response['Data']['content']));
    }

    getLeaveTypeDetails(pLeaveTypeId: number): Observable<LeaveType> {
        return this.getData(`${Endpoints.leave_type_details}/${pLeaveTypeId}`).pipe(map(response => response['Data']['LeaveType']));
    }

    saveLeaveType(pLeaveType: LeaveType): Observable<any> {
        return this.postData(pLeaveType.Id ? Endpoints.leave_type_edit : Endpoints.leave_type_add, pLeaveType);
    }

    setWeekendForUser(pWeekend: Weekend): Observable<any> {
        return this.postData(pWeekend.Id ? Endpoints.edit_weekend_request : Endpoints.set_weekend_request, pWeekend);
    }

    approveWeekendRequest(pApproval: Approval): Observable<any> {
        return this.postData(Endpoints.approve_weekend_request, pApproval);
    }

    getLeaveAllocationList(pParameters: any): Observable<LeaveAllocation[]> {
        return this.postData(Endpoints.leave_allocation_list, pParameters).pipe(map(response => response['Data']['content']));
    }

    getLeaveAllocationDetails(pLeaveAllocationId: number): Observable<LeaveAllocation> {
        return this.getData(`${Endpoints.leave_allocation_details}/${pLeaveAllocationId}`).pipe(map(response => response['Data']['LeaveAllocation']));
    }

    saveLeaveAllocation(pLeaveAllocation: LeaveAllocation): Observable<any> {
        return this.postData(pLeaveAllocation.Id ? Endpoints.leave_allocation_edit : Endpoints.leave_allocation_add, pLeaveAllocation);
    }

    getUserLeaveList(pParameters: any): Observable<UserLeave[]> {
        return this.postData(Endpoints.user_leave_list, pParameters).pipe(map(response => response['Data']['content']));
    }

    getUserLeaveDetails(pUserLeaveId: number): Observable<UserLeave> {
        return this.getData(`${Endpoints.user_leave_details}/${pUserLeaveId}`).pipe(map(response => response['Data']['UserLeave']));
    }

    saveUserLeave(pUserLeave: UserLeave): Observable<any> {
        return this.postData(pUserLeave.Id ? Endpoints.user_leave_edit : Endpoints.user_leave_add, pUserLeave);
    }

    saveTeamLeave(pUserLeave: UserLeave): Observable<any> {
        return this.postData(Endpoints.team_leave_add, pUserLeave);
    }

    updateUserAttendanceReference(pParameters: any): Observable<any> {
        return this.postData(Endpoints.update_user_attendance_reference, pParameters).pipe(map(response => response['Data']['Reference']));
    }


    // level
    saveLevel(pLevel: Level): Observable<Level> {
        return this.postData(pLevel.Id ? Endpoints.level_edit : Endpoints.level_add, pLevel);
    }

    getLevelDetails(pLevelId: number): Observable<Level> {
        return this.getData(`${Endpoints.level_details}/${pLevelId}`).pipe(map(response => response['Data']['Level']));
    }


    // location
    getUserLocationList(pParameters: any): Observable<TrackingLocation[]> {
        return this.postData(Endpoints.user_location_list, pParameters).pipe(map(response => response['Data']['content']));
    }

    getUserLocationListV2(pParameters: any): Observable<TrackingLocation[]> {
        return this.postData(Endpoints.user_location_list_v2, pParameters).pipe(map(response => response['Data']['content']));
    }

    getUserLocationTimeLine(pParameters: any): Observable<UserTimeline[]> {
        return this.postData(Endpoints.user_timeline, pParameters).pipe(map(response => response['Data']['content']));
    }

    getDistributorLocationList(pParameters: any): Observable<TrackingLocation[]> {
        return this.postData(Endpoints.distributor_location_list, pParameters).pipe(map(response => response['Data']['content']));
    }

    deleteDistributorLocation(pDistributorId: number): Observable<any> {
        return this.deleteData(`${Endpoints.distributor_location_delete}/${pDistributorId}`);
    }

    deleteCustomerLocation(pCustomerId: number): Observable<any> {
        return this.deleteData(`${Endpoints.customer_location_delete}/${pCustomerId}`);
    }

    getCustomerLocationList(pParameters: any): Observable<TrackingLocation[]> {
        return this.postData(Endpoints.customer_location_list, pParameters).pipe(map(response => response['Data']['content']));
    }

    getDealerLocationList(pParameters: any): Observable<TrackingLocation[]> {
        return this.postData(Endpoints.dealer_location_list, pParameters).pipe(map(response => response['Data']['content']));
    }

    getUserLastLocationList(pParameters: any): Observable<TrackingLocation[]> {
        return this.postData(Endpoints.user_last_location_list, pParameters).pipe(map(response => response['Data']['content']));
    }

    getMonitoringUserLastLocations(pParameters: any, pURL: string): Observable<MonitoringUserLastLocations[]> {
        return this.postDataForMicroService(pURL + Endpoints.monitoring_user_last_locations, pParameters).pipe(map(response => response['data']['content']));
    }

    getMonitorTeamTracking(pParameters: any, pURL: string): Observable<MonitorUserTimeline[]> {
        return this.postDataForMicroService(pURL + Endpoints.monitor_team_tracking, pParameters).pipe(map(response => response['data']['content']));
    }

    getMonitorUserTimelineByDateRange(pParameters: any, pURL: string): Observable<MonitorUserTimeline[]> {
        return this.postDataForMicroService(pURL + Endpoints.monitor_user_timeline_date_range, pParameters).pipe(map(response => response['data']['content']));
    }


    // market
    getMarketLevelList(pOrganizationId: number): Observable<MarketLevel[]> {
        return this.getData(`${Endpoints.market_level_list}?organizationId=${pOrganizationId}`).pipe(map(response => response['Data']['MarketLevelHierarchy']));
    }

    getMarketLevelDetails(pMarketLevelId: number): Observable<MarketLevel> {
        return this.getData(`${Endpoints.market_level_details}/${pMarketLevelId}`).pipe(map(response => response['Data']['MarketLevel']));
    }

    saveMarketLevel(pMarketLevel: MarketLevel): Observable<any> {
        return this.postData(pMarketLevel.Id ? Endpoints.market_level_edit : Endpoints.market_level_add, pMarketLevel);
    }

    deleteMarketLevel(pMarketLevelId: number): Observable<any> {
        return this.deleteData(`${Endpoints.market_level_delete}/${pMarketLevelId}`);
    }

    getMarketList(pParameters: any): Observable<Market[]> {
        return this.postData(Endpoints.market_list, pParameters).pipe(map(response => response['Data']['content']));
    }

    getMarketDetails(pMarketId: number): Observable<Market> {
        return this.getData(`${Endpoints.market_details}/${pMarketId}`).pipe(map(response => response['Data']['Market']));
    }

    saveMarket(pMarket: Market): Observable<any> {
        return this.postData(pMarket.Id ? Endpoints.market_edit : Endpoints.market_add, pMarket);
    }

    deleteMarket(pMarketId: number): Observable<any> {
        return this.deleteData(`${Endpoints.market_delete}/${pMarketId}`);
    }

    getMarketListMicroService(pParameters: any, pURL: string): Observable<MarketRef[]> {
        return this.postDataForMicroService(pURL + Endpoints.market_list_micro_service, pParameters).pipe(map(response => response['data']['content']));
    }


    // noc
    saveRetailerMonthlyNoc(pNoc: RetailerMonthlyNoc): Observable<any> {
        return this.postData(Endpoints.save_customer_monthly_noc, pNoc);
    }

    getRetailerMonthlyNocById(pUserId: number): Observable<RetailerMonthlyNoc> {
        return this.getData(`${Endpoints.get_customer_monthly_noc}/${pUserId}`).pipe(map(response => response['Data']['MonthlyNoc']));
    }


    // notice board
    getNoticeDetails(pId: number): Observable<NoticeBoard> {
        return this.getData(`${Endpoints.notice_details}/${pId}`).pipe(map(response => response['Data']['NoticeBoard']));
    }

    getNoticeBoardForOthersDetails(pId: number, pURL: string): Observable<NoticeBoardForOthers> {
        return this.getDataForMicroService(pURL + `${Endpoints.notice_board_for_others_details}/${pId}`, {}).pipe(map(response => response['data']));
    }

    changeNoticeBoardForOthersStatus(pApproval: ActivationStatus, pURL: string): Observable<any> {
        return this.postDataForMicroService(pURL + Endpoints.notice_board_for_others_active_status, pApproval);
    }

    saveNoticeBoardForOthers(pParameter: NoticeBoardForOthersManage, pURL: string): Observable<any> {
        return this.postDataForMicroService(pParameter.id ? pURL + Endpoints.notice_board_for_others_edit : pURL + Endpoints.notice_board_for_others_add, pParameter).pipe(map(response => response));
    }

    saveNoticeBoard(pNoticeBoard: NoticeBoard): Observable<NoticeBoard> {
        return this.postData(pNoticeBoard.Id ? Endpoints.notice_edit : Endpoints.notice_add, pNoticeBoard);
    }

    // income
    saveIncome(pParameter: IncomeManage, pURL: string): Observable<any> {
        return this.postDataForMicroService(pParameter.id ? pURL + Endpoints.income_edit : pURL + Endpoints.income_add, pParameter).pipe(map(response => response));
    }

    loadTotalCards(pParams: any, pURL: string): Observable<any> {
        return this.getDataForMicroService(pURL, pParams).pipe(map(response => response['data']));
    }

    cancelIncome(pId: number, pURL: string): Observable<any> {
        return this.postDataForMicroService(pURL + Endpoints.income_cancel, new ActivationStatus(pId, false));
    }

    // order
    getPrimaryOrderList(pParameters: any): Observable<PrimaryOrder[]> {
        return this.postData(Endpoints.primary_order_list, pParameters).pipe(map(response => response['Data']['content']));
    }

    getQuantityForPrimaryOrder(pParameters: any): Observable<any> {
        return this.postData(Endpoints.quantity_for_primary_order, pParameters).pipe(map(response => response['Data']['Quantity']));
    }

    getQuantityForSecondaryOrder(pParameters: any): Observable<any> {
        return this.postData(Endpoints.quantity_for_secondary_order, pParameters).pipe(map(response => response['Data']['Quantity']));
    }

    getPrimaryOrderDetails(pOrderId: number): Observable<PrimaryOrder> {
        return this.getData(`${Endpoints.primary_order_details}/${pOrderId}`).pipe(map(response => response['Data']['PrimaryOrder']));
    }


    savePrimaryOrder(pOrder: PrimaryOrder): Observable<any> {
        return this.postData(pOrder.Id ? Endpoints.primary_order_edit : Endpoints.primary_order_add, pOrder);
    }

    getPrimaryOrderPreview(pOrder: PrimaryOrder): Observable<PrimaryOrder> {
        return this.postData(Endpoints.primary_order_preview, pOrder).pipe(map(response => response['Data']['PrimaryOrder']));
    }

    getSecondaryOrderPreview(pOrder: SecondaryOrder): Observable<SecondaryOrder> {
        return this.postData(Endpoints.secondary_order_preview, pOrder).pipe(map(response => response['Data']['SecondaryOrder']));
    }

    getDealerOrderPreview(pOrder: DistributorDealerOrder): Observable<DistributorDealerOrder> {
        return this.postData(Endpoints.dealer_order_preview, pOrder).pipe(map(response => response['Data']['DealerOrder']));
    }

    getRetailerToDealerOrderPreview(pOrder: RetailerToDealerOrder): Observable<RetailerToDealerOrder> {
        return this.postData(Endpoints.retailer_to_dealer_order_preview, pOrder).pipe(map(response => response['Data']['RetailerToDealerOrder']));
    }


    getPrimaryOrderCampaignPreview(pOrder: PrimaryOrder): Observable<PrimaryOrder> {
        return this.postData(Endpoints.primary_order_campaign_preview, pOrder).pipe(map(response => response['Data']['PrimaryOrder']));
    }

    approvePrimaryOrder(pApproval: Approval): Observable<any> {
        return this.postData(Endpoints.primary_order_approve, pApproval);
    }


    closePrimaryOrder(pOrderId: number): Observable<PrimaryOrder> {
        return this.getData(`${Endpoints.primary_order_close}/${pOrderId}`).pipe(map(response => response));
    }

    getPrimaryOrderTracking(pOrderId: number): Observable<PrimaryOrderTracking> {
        return this.postData(Endpoints.primary_order_tracking, {OrderId: pOrderId}).pipe(map(response => response['Data']));
    }

    getSecondaryOrderTracking(pOrderId: number): Observable<SecondaryOrderTracking> {
        return this.postData(Endpoints.secondary_order_tracking, {OrderId: pOrderId}).pipe(map(response => response['Data']));
    }

    getSecondaryOrderList(pParameters: any): Observable<SecondaryOrder[]> {
        return this.postData(Endpoints.secondary_order_list, pParameters).pipe(map(response => response['Data']['content']));
    }

    getSecondaryOrderDetails(pOrderId: number): Observable<SecondaryOrder> {
        return this.getData(`${Endpoints.secondary_order_details}/${pOrderId}`).pipe(map(response => response['Data']['SecondaryOrder']));
    }

    getDistributorDealerOrderDetails(pOrderId: number): Observable<DistributorDealerOrder> {
        return this.getData(`${Endpoints.dealer_order_details}/${pOrderId}`).pipe(map(response => response['Data']['DealerOrder']));
    }

    getRetailerToDealerOrderDetails(pOrderId: number): Observable<RetailerToDealerOrder> {
        return this.getData(`${Endpoints.retailer_to_dealer_order_details}/${pOrderId}`).pipe(map(response => response['Data']['RetailerToDealerOrder']));
    }

    saveSecondaryOrder(pOrder: SecondaryOrder): Observable<any> {
        return this.postData(pOrder.Id ? Endpoints.secondary_order_edit : Endpoints.secondary_order_add, pOrder);
    }

    saveDealerOrder(pOrder: DistributorDealerOrder): Observable<any> {
        return this.postData(pOrder.Id ? Endpoints.dealer_order_edit : Endpoints.dealer_order_add, pOrder);
    }

    saveRetailerToDealerOrder(pOrder: RetailerToDealerOrder): Observable<any> {
        return this.postData(pOrder.Id ? Endpoints.retailer_to_dealer_order_edit : Endpoints.retailer_to_dealer_order_add, pOrder);
    }

    getSecondaryOrderCampaignPreview(pOrder: SecondaryOrder): Observable<SecondaryOrder> {
        return this.postData(Endpoints.secondary_order_campaign_preview, pOrder).pipe(map(response => response['Data']['SecondaryOrder']));
    }

    approveSecondaryOrder(pApproval: Approval): Observable<any> {
        return this.postData(Endpoints.secondary_order_approve, pApproval);
    }

    approveDistributorDealerOrder(pApproval: Approval): Observable<any> {
        return this.postData(Endpoints.dealer_order_approve, pApproval);
    }

    approveRetailerToDealerOrder(pApproval: Approval): Observable<any> {
        return this.postData(Endpoints.retailer_to_dealer_order_approve, pApproval);
    }

    closeRetailerToDealerOrder(pRetailerToDealerOrderId: number): Observable<any> {
        return this.getData(`${Endpoints.retailer_to_order_close}/${pRetailerToDealerOrderId}`);
    }

    closeSecondaryOrder(pSecondaryOrderId: number): Observable<any> {
        return this.getData(`${Endpoints.secondary_order_close}/${pSecondaryOrderId}`);
    }

    closeDealerOrder(pDealerOrderId: number): Observable<any> {
        return this.getData(`${Endpoints.dealer_order_close}/${pDealerOrderId}`);
    }

    getCreditOptionList(pParameters: any): Observable<CreditOption[]> {
        return this.postData(Endpoints.creditOption_list, pParameters).pipe(map(response => response['Data']['content']));
    }

    getCreditOptionDetails(pExpenseTypeId: number): Observable<CreditOption> {
        return this.getData(`${Endpoints.creditOption_details}/${pExpenseTypeId}`).pipe(map(response => response['Data']['CreditOption']));
    }

    saveCreditOption(pExpenseType: CreditOption): Observable<any> {
        return this.postData(pExpenseType.Id ? Endpoints.creditOption_edit : Endpoints.creditOption_add, pExpenseType);
    }


    getMarketListByDistributor(pParameters: any): Observable<Market[]> {
        return this.postData(Endpoints.market_list_by_distributor, pParameters).pipe(map(response => response['Data']['content']));
    }

    getMarketListByCustomer(pParameters: any): Observable<Market[]> {
        return this.postData(Endpoints.market_list_by_customer, pParameters).pipe(map(response => response['Data']['content']));
    }

    getMarketListByDistributorAndCustomer(pParameters: any): Observable<Market[]> {
        return this.postData(Endpoints.market_list_by_distributor_and_customer, pParameters).pipe(map(response => response['Data']['content']));
    }

    getMarketListByDealerAndCustomer(pParameters: any): Observable<Market[]> {
        return this.postData(Endpoints.market_list_by_customer_and_dealer, pParameters).pipe(map(response => response['Data']['content']));
    }

    getMarketListByDistributorAndDealer(pParameters: any): Observable<Market[]> {
        return this.postData(Endpoints.market_list_by_distributor_and_dealer, pParameters).pipe(map(response => response['Data']['content']));
    }

    getSecondaryOrderPreviewForAkijPlastics(pOrderRequest: SecondaryOrderRequest, pMicroServiceUrl: string): Observable<SecondaryOrderPreview> {
        return this.postDataForMicroService(pMicroServiceUrl + Endpoints.akij_plastics_secondary_order_preview, pOrderRequest).pipe(map(response => response['data']));
    }

    getSecondaryOrderForMicroServiceDetails(pId: number, pURL: string): Observable<SecondaryOrderDetails> {
        return this.postDataForMicroService(pURL + `${Endpoints.akij_plastic_secondary_order_details}/${pId}`, {}).pipe(map(response => response['data']));
    }

    approveSecondaryOrderForMicroService(pApproval: ApprovalForMicroService, pURL: string): Observable<any> {
        return this.postDataForMicroService(pURL + Endpoints.akij_plastic_secondary_order_approve, pApproval).pipe(map(response => response));
    }

    saveSecondaryOrderForMicroService(pSecondaryOrder: SecondaryOrderRequest, pURL: string): Observable<any> {
        return this.postDataForMicroService(pURL + (pSecondaryOrder.id ? Endpoints.akij_plastic_secondary_order_edit : Endpoints.akij_plastic_secondary_order_add), pSecondaryOrder).pipe(map(response => response));
    }

    resetSecondaryOrderForMicroService(pId: number, pURL: string): Observable<SecondaryOrderDetails> {
        return this.postDataForMicroService(pURL + `${Endpoints.akij_plastic_secondary_order_reset}/${pId}`, {}).pipe(map(response => response['data']));
    }

    // fridge
    getFridgeDetails(pId: number, pURL: string): Observable<Fridge> {
        return this.getDataForMicroService(pURL + `${Endpoints.fridge_details}/${pId}`, {}).pipe(map(response => response['data']));
    }

    getFridgeByRetailerDetails(pId: number, pURL: string): Observable<Fridge[]> {
        return this.getDataForMicroService(pURL + `${Endpoints.fridge_by_retailer}/${pId}`, {}).pipe(map(response => response['data']['content']));
    }

    saveFridge(pData: Fridge, pURL: string): Observable<any> {
        if (pData.id) {
            return this.putDataForMicroService( pURL + Endpoints.fridge_edit, pData).pipe(map(response => response['data']));
        }else {
            return this.postDataForMicroService( pURL + Endpoints.fridge_add, pData).pipe(map(response => response['data']));
        }
    }

    withdrawFridge(pId: number, pURL: string): Observable<any> {
        return this.patchData(pURL + `${Endpoints.fridge_withdraw}/${pId}`, {}).pipe(map(response => response['data']['content']));
    }

    injectFridge(pData: Fridge, pURL: string): Observable<any> {
        return this.postDataForMicroService( ( pURL + Endpoints.fridge_inject), pData).pipe(map(response => response['data']));
    }

    getStockSurveyDetails(pId: number, pURL: string): Observable<StockSurvey> {
        return this.getDataForMicroService(pURL + `${Endpoints.stock_survey_details}/${pId}`, {}).pipe(map(response => response['data']));
    }

    getAvailabilityAndInfoBySerial(pId: any, pURL: string): Observable<Fridge> {
        return this.getDataForMicroService(pURL + `${Endpoints.fridge_availability_and_info_by_serial}/${pId}`, {}).pipe(map(response => response['data']));
    }


    // asset
    getAssetDetails(pId: number, pURL: string): Observable<Asset> {
        return this.getDataForMicroService(pURL + `${Endpoints.asset_details}/${pId}`, {}).pipe(map(response => response['data']));
    }

    saveAsset(pData: Asset, pURL: string): Observable<any> {
        if (pData.id) {
            return this.putDataForMicroService( pURL + Endpoints.asset_edit, pData).pipe(map(response => response['data']));
        }else {
            return this.postDataForMicroService( pURL + Endpoints.asset_add, pData).pipe(map(response => response['data']));
        }
    }

    saveAssetType(pData: AssetType, pURL: string): Observable<any> {
        if (pData.id) {
            return this.putDataForMicroService( pURL + Endpoints.asset_type_edit, pData).pipe(map(response => response['data']));
        }else {
            return this.postDataForMicroService( pURL + Endpoints.asset_type_add, pData).pipe(map(response => response['data']));
        }
    }

    saveAssetCondition(pData: AssetType, pURL: string): Observable<any> {
        if (pData.id) {
            return this.putDataForMicroService( pURL + Endpoints.asset_condition_edit, pData).pipe(map(response => response['data']));
        }else {
            return this.postDataForMicroService( pURL + Endpoints.asset_condition_add, pData).pipe(map(response => response['data']));
        }
    }

    withdrawAsset(pId: number, pURL: string): Observable<any> {
        return this.patchData(pURL + `${Endpoints.asset_withdraw}/${pId}`, {}).pipe(map(response => response['data']['content']));
    }

    injectAsset(pData: Asset, pURL: string): Observable<any> {
        return this.postDataForMicroService( ( pURL + Endpoints.asset_inject), pData).pipe(map(response => response['data']));
    }

    getAvailabilityAndInfoBySerialAsset(pId: any, pURL: string): Observable<Asset> {
        return this.getDataForMicroService(pURL + `${Endpoints.asset_availability_and_info_by_serial}/${pId}`, {}).pipe(map(response => response['data']));
    }

    // poi
    editPoi(pPoi: Poi): Observable<any> {
        return this.postData(pPoi.Id ? Endpoints.edit_poi : Endpoints.create_poi, pPoi);
    }

    getPoiTypeList(pParameters: any): Observable<Type[]> {
        if (this.accessToken) {
            return this.postData(Endpoints.poi_type_list, pParameters).pipe(map(response => response['Data']['content']));
        } else {
            return EMPTY;
        }
    }

    getPoiList(pParameters: any): Observable<Poi[]> {
        return this.postData(Endpoints.poi_list, pParameters).pipe(map(response => response['Data']['content']));
    }

    updateUserPoi(pParameters: any): Observable<any> {
        return this.postData(Endpoints.user_poi_update, pParameters).pipe(map(response => response['Data']['content']));
    }

    updatePoiLocation(pParameters: any): Observable<any> {
        return this.postData(Endpoints.update_poi_location, pParameters);
    }

    getPoiDetails(pPoiId: number): Observable<Poi> {
        return this.getData(`${Endpoints.poi_details}/${pPoiId}`).pipe(map(response => response['Data']['Customer']));
    }


    // payment
    getPaymentPurposeList(pParameters: any): Observable<PaymentPurpose[]> {
        return this.postData(Endpoints.payment_purpose_list, pParameters).pipe(map(response => response['Data']['content']));
    }

    getPaymentPurposeDetails(pPaymentPurposeId: number): Observable<PaymentPurpose> {
        return this.getData(`${Endpoints.payment_purpose_details}/${pPaymentPurposeId}`).pipe(map(response => response['Data']['PaymentPurpose']));
    }

    savePaymentPurpose(pPaymentPurpose: PaymentPurpose): Observable<any> {
        return this.postData(pPaymentPurpose.Id ? Endpoints.payment_purpose_edit : Endpoints.payment_purpose_add, pPaymentPurpose);
    }

    getPaymentCategoryList(pParameters: any): Observable<PaymentCategory[]> {
        return this.postData(Endpoints.payment_category_list, pParameters).pipe(map(response => response['Data']['content']));
    }


    getPaymentCategoryDetails(pPaymentCategoryId: number): Observable<PaymentCategory> {
        return this.getData(`${Endpoints.payment_category_details}/${pPaymentCategoryId}`).pipe(map(response => response['Data']['PaymentCategory']));
    }

    savePaymentCategory(pPaymentCategory: PaymentCategory): Observable<any> {
        return this.postData(pPaymentCategory.Id ? Endpoints.payment_category_edit : Endpoints.payment_category_add, pPaymentCategory);
    }

    getPaymentMethodList(pParameters: any): Observable<PaymentMethod[]> {
        return this.postData(Endpoints.payment_method_list, pParameters).pipe(map(response => response['Data']['content']));
    }

    getPaymentMethodDetails(pPaymentMethodId: number): Observable<PaymentMethod> {
        return this.getData(`${Endpoints.payment_method_details}/${pPaymentMethodId}`).pipe(map(response => response['Data']['PaymentMethod']));
    }

    savePaymentMethod(pPaymentMethod: PaymentMethod): Observable<any> {
        return this.postData(pPaymentMethod.Id ? Endpoints.payment_method_edit : Endpoints.payment_method_add, pPaymentMethod);
    }

    uploadBank(pBank: BankUpload): Observable<any> {
        return this.postData(Endpoints.bank_upload, pBank);
    }

    changeActiveStatus(pApproval: UserStatusChange): Observable<any> {
        return this.putData(Endpoints.change_active_status, pApproval);
    }

    saveBank(pBank: Bank): Observable<any> {
        return this.postData(Endpoints.bank_add, pBank);
    }

    updateBank(pBank: Bank): Observable<any> {
        return this.putData(Endpoints.bank_update, pBank);
    }

    getOrganizationBankAccountList(pParameters: any): Observable<OrganizationBankAccount[]> {
        return this.postData(Endpoints.org_bank_account_list, pParameters).pipe(map(response => response['Data']['content']));
    }

    getOrganizationBankAccountDetails(pOrganizationBankAccountId: number): Observable<OrganizationBankAccount> {
        return this.getData(`${Endpoints.org_bank_account_details}/${pOrganizationBankAccountId}`).pipe(map(response => response['Data']['OrganizationBankAccount']));
    }

    saveOrganizationBankAccount(pOrganizationBankAccount: OrganizationBankAccount): Observable<any> {
        return this.postData(pOrganizationBankAccount.Id ? Endpoints.org_bank_account_edit : Endpoints.org_bank_account_add, pOrganizationBankAccount);
    }

    approveOrganizationBankAccount(pApproval: Approval): Observable<any> {
        return this.postData(Endpoints.org_bank_account_approve, pApproval);
    }

    deleteOrganizationBankAccount(pOrganizationBankAccountId: number): Observable<any> {
        return this.deleteData(`${Endpoints.org_bank_account_delete}/${pOrganizationBankAccountId}`);
    }


    // product
    getProductList(pParameters: any): Observable<Product[]> {
        return this.postData(Endpoints.product_list, pParameters).pipe(map(response => response['Data']['content']));
    }

    getProductDetails(pProductId: number): Observable<Product> {
        return this.getData(`${Endpoints.product_details}/${pProductId}`).pipe(map(response => response['Data']['Product']));
    }

    saveProduct(pProduct: Product): Observable<any> {
        return this.postData(pProduct.Id ? Endpoints.product_edit : Endpoints.product_add, pProduct);
    }

    changeProductForDealerStatus(pApproval: ProductStatusChange): Observable<any> {
        return this.postData(Endpoints.product_status_dealer, pApproval);
    }

    changeProductForDistributorStatus(pApproval: ProductStatusChange): Observable<any> {
        return this.postData(Endpoints.product_status_distributor, pApproval);
    }

    changeProductForRetailerStatus(pApproval: ProductStatusChange): Observable<any> {
        return this.postData(Endpoints.product_status_retailer, pApproval);
    }

    changeProductStatus(pApproval: ProductStatusChange): Observable<any> {
        return this.postData(Endpoints.product_status, pApproval);
    }

    getProductLineList(pParameters: any): Observable<ProductLine[]> {
        return this.postData(Endpoints.product_line_list, pParameters).pipe(map(response => response['Data']['content']));
    }

    saveTag(pTag: Tag): Observable<any> {
        return this.postData(pTag.Id ? Endpoints.tag_edit : Endpoints.tag_add, pTag);
    }

    getProductLineDetails(pProductLineId: number): Observable<ProductLine> {
        return this.getData(`${Endpoints.product_line_details}/${pProductLineId}`).pipe(map(response => response['Data']['ProductLine']));
    }

    saveProductLine(pProductLine: ProductLine): Observable<any> {
        return this.postData(pProductLine.Id ? Endpoints.product_line_edit : Endpoints.product_line_add, pProductLine);
    }


    getProductGroupList(pParameters: any): Observable<ProductGroup[]> {
        return this.postData(Endpoints.product_group_list, pParameters).pipe(map(response => response['Data']['content']));
    }

    getProductGroupDetails(pProductGroupId: number): Observable<ProductGroup> {
        return this.getData(`${Endpoints.product_group_details}/${pProductGroupId}`).pipe(map(response => response['Data']['ProductGroup']));
    }

    saveProductGroup(pProductGroup: ProductGroup): Observable<any> {
        return this.postData(pProductGroup.Id ? Endpoints.product_group_edit : Endpoints.product_group_add, pProductGroup);
    }


    getProductBrandList(pParameters: any): Observable<ProductBrand[]> {
        return this.postData(Endpoints.product_brand_list, pParameters).pipe(map(response => response['Data']['content']));
    }

    getProductBrandDetails(pProductBrandId: number): Observable<ProductBrand> {
        return this.getData(`${Endpoints.product_brand_details}/${pProductBrandId}`).pipe(map(response => response['Data']['ProductBrand']));
    }

    saveProductBrand(pProductBrand: ProductBrand): Observable<any> {
        return this.postData(pProductBrand.Id ? Endpoints.product_brand_edit : Endpoints.product_brand_add, pProductBrand);
    }


    getProductTypeList(pParameters: any): Observable<ProductType[]> {
        return this.postData(Endpoints.product_type_list, pParameters).pipe(map(response => response['Data']['content']));
    }


    getProductTypeDetails(pProductTypeId: number): Observable<ProductType> {
        return this.getData(`${Endpoints.product_type_details}/${pProductTypeId}`).pipe(map(response => response['Data']['ProductType']));
    }

    saveProductType(pProductType: ProductType): Observable<any> {
        return this.postData(pProductType.Id ? Endpoints.product_type_edit : Endpoints.product_type_add, pProductType);
    }

    getTagList(pParameters: any): Observable<Tag[]> {
        return this.postData(Endpoints.tag_list, pParameters).pipe(map(response => response['Data']['content']));
    }

    getTagDetails(pTagId: number): Observable<Tag> {
        return this.getData(`${Endpoints.tag_details}/${pTagId}`).pipe(map(response => response['Data']['Tag']));
    }


    // project

    approveProject(pParameters: any): Observable<any> {
        return this.postData(Endpoints.project_approve, pParameters);
    }

    saveProject(pProject: Project): Observable<Project> {
        return this.postData(pProject.Id ? Endpoints.project_edit : Endpoints.project_add, pProject);
    }

    getProjectDetails(pId: number): Observable<Project> {
        return this.getData(`${Endpoints.project_details}/${pId}`).pipe(map(response => response['Data']['Project']));
    }

    getProjectOwnerTypeList(pParameters: any): Observable<ProjectOwnerType[]> {
        return this.postData(Endpoints.project_owner_type_list, pParameters).pipe(map(response => response['Data']['content']));
    }

    saveProjectType(pProjectType: ProjectType): Observable<ProjectType> {
        return this.postData(pProjectType.Id ? Endpoints.project_type_edit : Endpoints.project_type_add, pProjectType);
    }

    saveProjectStatus(pProjectStatus: ProjectStatus): Observable<ProjectStatus> {
        return this.postData(pProjectStatus.Id ? Endpoints.project_status_edit : Endpoints.project_status_add, pProjectStatus);
    }

    saveProjectDemand(pProjectDemand: ProjectDemand): Observable<ProjectDemand> {
        return this.postData(pProjectDemand.Id ? Endpoints.project_demand_edit : Endpoints.project_demand_add, pProjectDemand);
    }

    saveProjectConsultant(pProjectConsultant: ProjectConsultant): Observable<ProjectConsultant> {
        return this.postData(pProjectConsultant.Id ? Endpoints.project_consultant_edit : Endpoints.project_consultant_add, pProjectConsultant);
    }

    saveProjectProperties(pProjectProperties: ProjectProperties): Observable<ProjectProperties> {
        return this.postData(pProjectProperties.Id ? Endpoints.project_properties_edit : Endpoints.project_properties_add, pProjectProperties);
    }

    saveProjectPropertiesValue(pProjectPropertiesValue: ProjectPropertiesValue): Observable<ProjectPropertiesValue> {
        return this.postData(pProjectPropertiesValue.Id ? Endpoints.project_properties_value_edit : Endpoints.project_properties_value_add, pProjectPropertiesValue);
    }

    saveProjectOwnerType(pProjectOwnerType: ProjectOwnerType): Observable<ProjectOwnerType> {
        return this.postData(pProjectOwnerType.Id ? Endpoints.project_owner_type_edit : Endpoints.project_owner_type_add, pProjectOwnerType);
    }

    saveProjectDemandDocumentType(pProjectDemandDocumentType: ProjectDemandDocumentType): Observable<ProjectDemandDocumentType> {
        return this.postData(pProjectDemandDocumentType.Id ? Endpoints.project_demand_document_type_edit : Endpoints.project_demand_document_type_add, pProjectDemandDocumentType);
    }

    getProjectTypeDetails(pId: number): Observable<ProjectType> {
        return this.getData(`${Endpoints.project_type_details}/${pId}`).pipe(map(response => response['Data']['ProjectType']));
    }

    getProjectStatusDetails(pId: number): Observable<ProjectStatus> {
        return this.getData(`${Endpoints.project_status_details}/${pId}`).pipe(map(response => response['Data']['ProjectStatus']));
    }

    getProjectConsultantDetails(pId: number): Observable<ProjectConsultant> {
        return this.getData(`${Endpoints.project_consultant_details}/${pId}`).pipe(map(response => response['Data']['ProjectConsultant']));
    }

    getProjectPropertiesDetails(pId: number): Observable<ProjectProperties> {
        return this.getData(`${Endpoints.project_properties_details}/${pId}`).pipe(map(response => response['Data']['ProjectProperties']));
    }

    getProjectPropertiesValueDetails(pId: number): Observable<ProjectPropertiesValue> {
        return this.getData(`${Endpoints.project_properties_value_details}/${pId}`).pipe(map(response => response['Data']['ProjectProperties']));
    }

    getProjectDemandDetails(pId: number): Observable<ProjectDemand> {
        return this.getData(`${Endpoints.project_demand_details}/${pId}`).pipe(map(response => response['Data']['ProjectDemand']));
    }

    getProjectOwnerTypeDetails(pId: number): Observable<ProjectOwnerType> {
        return this.getData(`${Endpoints.project_owner_type_details}/${pId}`).pipe(map(response => response['Data']['ProjectOwnerType']));
    }

    getProjectDemandDocumentTypeDetails(pId: number): Observable<ProjectDemandDocumentType> {
        return this.getData(`${Endpoints.project_demand_document_type_details}/${pId}`).pipe(map(response => response['Data']['ProjectDocumentType']));
    }

    getProjectTypeList(pParameters: any): Observable<ProjectType[]> {
        return this.postData(Endpoints.project_type_list, pParameters).pipe(map(response => response['Data']['content']));
    }

    getProjectList(pParameters: any): Observable<Project[]> {
        return this.postData(Endpoints.project_list, pParameters).pipe(map(response => response['Data']['content']));
    }

    getProjectConsultantList(pParameters: any): Observable<ProjectConsultant[]> {
        return this.postData(Endpoints.project_consultant_list, pParameters).pipe(map(response => response['Data']['content']));
    }

    getProjectPropertiesList(pParameters: any): Observable<ProjectProperties[]> {
        return this.postData(Endpoints.project_properties_list, pParameters).pipe(map(response => response['Data']['content']));
    }

    getProjectPropertiesValueList(pParameters: any): Observable<ProjectPropertiesValue[]> {
        return this.postData(Endpoints.project_properties_value_list, pParameters).pipe(map(response => response['Data']['content']));
    }

    getProjectDemandDocumentTypeList(pParameters: any): Observable<ProjectDemandDocumentType[]> {
        return this.postData(Endpoints.project_demand_document_type_list, pParameters).pipe(map(response => response['Data']['content']));
    }

    getProjectStatusList(pParameters: any): Observable<ProjectStatus[]> {
        return this.postData(Endpoints.project_status_list, pParameters).pipe(map(response => response['Data']['content']));
    }


    // retailer
    approveCustomer(pApproval: Approval): Observable<any> {
        return this.postData(Endpoints.customer_approve, pApproval);
    }

    getCustomerPaymentList(pParameters: any): Observable<CustomerPayment[]> {
        return this.postData(Endpoints.customer_payment_list, pParameters).pipe(map(response => response['Data']['content']));
    }

    getCustomerPaymentDetails(pCustomerPaymentId: number): Observable<CustomerPayment> {
        return this.getData(`${Endpoints.customer_payment_details}/${pCustomerPaymentId}`).pipe(map(response => response['Data']['CustomerPayment']));
    }

    saveCustomerPayment(pCustomerPayment: CustomerPayment): Observable<any> {
        return this.postData(pCustomerPayment.Id ? Endpoints.customer_payment_edit : Endpoints.customer_payment_add, pCustomerPayment);
    }

    saveDealerPayment(pDealerPayment: DealerPayment): Observable<any> {
        return this.postData(pDealerPayment.Id ? Endpoints.dealer_payment_edit : Endpoints.dealer_payment_add, pDealerPayment);
    }

    approveCustomerPayment(pApproval: Approval): Observable<any> {
        return this.postData(Endpoints.customer_payment_approve, pApproval);
    }

    getCustomerTypeList(pParameters: any): Observable<CustomerType[]> {
        return this.postData(Endpoints.customer_type_list, pParameters).pipe(map(response => response['Data']['content']));
    }

    getCustomerTypeDetails(pCustomerTypeId: number): Observable<CustomerType> {
        return this.getData(`${Endpoints.customer_type_details}/${pCustomerTypeId}`).pipe(map(response => response['Data']['CustomerType']));
    }

    saveCustomerType(pCustomerType: CustomerType): Observable<any> {
        return this.postData(pCustomerType.Id ? Endpoints.customer_type_edit : Endpoints.customer_type_add, pCustomerType);
    }

    getCustomerList(pParameters: any): Observable<Customer[]> {
        return this.postData(Endpoints.customer_list, pParameters).pipe(map(response => response['Data']['content']));
    }

    getCustomerDetails(pCustomerId: number): Observable<Customer> {
        return this.getData(`${Endpoints.customer_details}/${pCustomerId}`).pipe(map(response => response['Data']['Customer']));
    }

    saveCustomer(pCustomer: Customer): Observable<any> {
        return this.postData(pCustomer.Id ? Endpoints.customer_edit : Endpoints.customer_add, pCustomer);
    }

    changeRetailerStatus(pApproval: UserStatusChange): Observable<any> {
        return this.postData(Endpoints.change_customer_status, pApproval);
    }

    // return
    getCustomerReasonList(pParameters: any): Observable<CustomerReturnReason[]> {
        return this.postData(Endpoints.customer_return_reason_list, pParameters).pipe(map(response => response['Data']['content']));
    }

    getDistributorReasonList(pParameters: any): Observable<DistributorReturnReason[]> {
        return this.postData(Endpoints.distributor_return_reason_list, pParameters).pipe(map(response => response['Data']['content']));
    }

    getCustomerReturnReasonDetails(pId: number): Observable<CustomerReturnReason> {
        return this.getData(`${Endpoints.customer_return_reason_details}/${pId}`).pipe(map(response => response['Data']['ReturnReason']));
    }

    getDistributorReturnReasonDetails(pId: number): Observable<DistributorReturnReason> {
        return this.getData(`${Endpoints.distributor_return_reason_details}/${pId}`).pipe(map(response => response['Data']['ReturnReason']));
    }

    saveCustomerReturnReason(pCustomerReturnReason: CustomerReturnReason): Observable<any> {
        return this.postData(pCustomerReturnReason.Id ? Endpoints.customer_return_reason_edit : Endpoints.customer_return_reason_add, pCustomerReturnReason);
    }

    saveDistributorReturnReason(pCustomerReturnReason: DistributorReturnReason): Observable<any> {
        return this.postData(pCustomerReturnReason.Id ? Endpoints.distributor_return_reason_edit : Endpoints.distributor_return_reason_add, pCustomerReturnReason);
    }

    getCustomerReturnList(pParameters: any): Observable<CustomerReturn[]> {
        return this.postData(Endpoints.customer_return_list, pParameters).pipe(map(response => response['Data']['content']));
    }

    getCustomerReturnDetails(pReturnId: number): Observable<CustomerReturn> {
        return this.getData(`${Endpoints.customer_return_details}/${pReturnId}`).pipe(map(response => response['Data']['CustomerReturn']));
    }

    saveCustomerReturn(pReturn: any): Observable<any> {
        return this.postData(Endpoints.customer_return_add, pReturn);
    }

    approveCustomerReturn(pApproval: Approval): Observable<any> {
        return this.postData(Endpoints.customer_return_approve, pApproval);
    }

    approveCustomerReturnBySerial(pApproval: Approval): Observable<any> {
        return this.postData(Endpoints.customer_return_approve_by_serial, pApproval);
    }

    getGeneralReturnDetails(pId: number, pURL: string): Observable<GeneralReturn> {
        return this.getDataForMicroService(pURL + `${Endpoints.general_return_details}/${pId}`, {}).pipe(map(response => response['data']));
    }

    approveGeneralReturn(pApproval: ApprovalForMicroService,  pURL: string): Observable<any> {
        return this.postDataForMicroService(pURL + Endpoints.general_return_approve, pApproval);
    }

    resetGeneralReturn(pParameters: any, pURL: string): Observable<TaClaim> {
        return this.postDataForMicroService(pURL + Endpoints.general_return_reset, pParameters).pipe(map(response => response['data']['content']));
    }

    editGeneralReturn(pParameters: any, pURL: string): Observable<any> {
        return this.postDataForMicroService(pURL + Endpoints.general_return_edit, pParameters).pipe(map(response => response['data']));
    }

    createGeneralReturn(pParameters: any, pURL: string): Observable<any> {
        return this.postDataForMicroService(pURL + Endpoints.general_return_add, pParameters).pipe(map(response => response['data']));
    }

    getDistributorReturnList(pParameters: any): Observable<DistributorReturn[]> {
        return this.postData(Endpoints.distributor_return_list, pParameters).pipe(map(response => response['Data']['content']));
    }

    getDistributorReturnDetails(pReturnId: number): Observable<DistributorReturn> {
        return this.getData(`${Endpoints.distributor_return_details}/${pReturnId}`).pipe(map(response => response['Data']['DistributorReturn']));
    }

    saveDistributorReturn(pReturn: any): Observable<any> {
        return this.postData(Endpoints.distributor_return_add, pReturn);
    }

    editDistributorReturn(pReturn: any): Observable<any> {
        return this.postData(Endpoints.distributor_return_edit, pReturn);
    }

    approveDistributorReturn(pApproval: Approval): Observable<any> {
        return this.postData(Endpoints.distributor_return_approve, pApproval);
    }

    approveDistributorReturnBySerial(pApproval: Approval): Observable<any> {
        return this.postData(Endpoints.distributor_return_approve_by_serial, pApproval);
    }

    saveCustomerReturnRefund(pCustomerReturn: any): Observable<any> {
        return this.postData(Endpoints.customer_return_refund, pCustomerReturn);
    }

    saveDistributorReturnRefund(pReturn: any): Observable<any> {
        return this.postData(Endpoints.distributor_return_refund, pReturn);
    }


    // sample requisition
    getDistributorSampleRequisition(pOrderId: number): Observable<SampleRequisition> {
        return this.getData(`${Endpoints.distributor_sample_requisition_details}/${pOrderId}`).pipe(map(response => response['Data']['SampleRequisition']));
    }

    getSampleRequisitionDetails(pOrderId: number): Observable<SampleRequisition> {
        return this.getData(`${Endpoints.distributor_sample_requisition_details}/${pOrderId}`).pipe(map(response => response['Data']['SampleRequisition']));
    }

    approveSampleRequisition(pApproval: SampleRequisitionApproval): Observable<any> {
        return this.postData(Endpoints.approve_sample_requisition, pApproval);
    }


    // scheme
    getSchemeDetails(pId: number): Observable<Scheme> {
        return this.getData(`${Endpoints.scheme_details}/${pId}`).pipe(map(response => response['Data']['Scheme']));
    }

    saveScheme(pTraining: Scheme): Observable<Scheme> {
        return this.postData(pTraining.Id ? Endpoints.scheme_edit : Endpoints.scheme_add, pTraining);
    }


    // segment
    getSegmentList(pParameters: any): Observable<Segment[]> {
        return this.postData(Endpoints.segment_list, pParameters).pipe(map(response => response['Data']['content']));
    }

    getSegmentDetails(pSegmentId: number): Observable<Segment> {
        return this.getData(`${Endpoints.segment_details}/${pSegmentId}`).pipe(map(response => response['Data']['Segment']));
    }

    saveSegment(pSegment: Segment): Observable<any> {
        return this.postData(pSegment.Id ? Endpoints.segment_edit : Endpoints.segment_add, pSegment);
    }

    getSubSegmentDetails(pSubSegmentId: number): Observable<SubSegment> {
        return this.getData(`${Endpoints.sub_segment_details}/${pSubSegmentId}`).pipe(map(response => response['Data']['SubSegment']));
    }

    saveSubSegment(pSegment: SubSegment): Observable<any> {
        return this.postData(pSegment.Id ? Endpoints.sub_segment_edit : Endpoints.sub_segment_add, pSegment);
    }

    getSubSegmentList(pParameters: any): Observable<SubSegment[]> {
        return this.postData(Endpoints.sub_segment_list, pParameters).pipe(map(response => response['Data']['content']));
    }


    // target achievement
    editKpiTarget(pKpiTargetAchievement: KpiTargetAchievement): Observable<any> {
        return this.postData(Endpoints.edit_kpi_target_achievement, pKpiTargetAchievement);
    }

    editRetailerKpiTarget(pRetailerKpiTargetAchievement: RetailerKpiTargetAchievement): Observable<any> {
        return this.postData(Endpoints.edit_retailer_kpi_target_achievement, pRetailerKpiTargetAchievement);
    }

    getDynamicKpiNameList(pParameters: any): Observable<[]> {
        return this.postData(Endpoints.dynamic_kpi_name_list, pParameters).pipe(map(response => response['Data']['content']));
    }


    // target achievement
    getPrimaryMarketSalesTarget(pParameters: any): Observable<any> {
        return this.postData(Endpoints.primary_market_sales_target_list, pParameters).pipe(map(response => response['Data']['content']));
    }

    getPrimaryMarketSalesTargetByProductGroup(pParameters: any): Observable<any> {
        return this.postData(Endpoints.product_group_primary_market_sales_target, pParameters).pipe(map(response => response['Data']['content']));
    }

    getSecondaryMarketSalesTargetByProductGroup(pParameters: any): Observable<any> {
        return this.postData(Endpoints.product_group_secondary_market_sales_target, pParameters).pipe(map(response => response['Data']['content']));
    }

    getSecondaryMarketSalesTargetByProduct(pParameters: any): Observable<any> {
        return this.postData(Endpoints.product_secondary_market_sales_target, pParameters).pipe(map(response => response['Data']['content']));
    }

    getPrimaryMarketSalesTargetByProduct(pParameters: any): Observable<any> {
        return this.postData(Endpoints.product_primary_market_sales_target, pParameters).pipe(map(response => response['Data']['content']));
    }

    getSecondaryMarketSalesTarget(pParameters: any): Observable<any> {
        return this.postData(Endpoints.secondary_market_sales_target_list, pParameters).pipe(map(response => response['Data']['content']));
    }

    getMarketTargetReports(pParameters: any): Observable<MarketTargetReport> {
        return this.postData(Endpoints.market_target_report_list, pParameters).pipe(map(response => response['Data']['content']));
    }

    getMarketTargetReportDetails(pMarketTargetId: number): Observable<MarketTargetReport> {
        return this.getData(`${Endpoints.market_target_report_details}/${pMarketTargetId}`).pipe(map(response => response['Data']['Info']));
    }

    saveMarketTargetReport(pMarketTargetReport: MarketTargetReport): Observable<any> {
        return this.postData(pMarketTargetReport.Id ? Endpoints.update_market_target_report : Endpoints.add_market_target_report, pMarketTargetReport);
    }

    saveMarketTarget(pMarketTarget: MarketTargetSet): Observable<any> {
        return this.postData(pMarketTarget.Id ? Endpoints.update_market_target : Endpoints.add_market_target, pMarketTarget);
    }

    uploadMarketTarget(pMarketTarget: MarketTargetSet): Observable<any> {
        return this.postData(Endpoints.upload_market_target, pMarketTarget);
    }

    uploadMarketProductTarget(pMarketProductTarget: MarketProductTargetUpload, pUrl: string): Observable<any> {
        return this.postDataForMicroService(pUrl + Endpoints.upload_market_product_target, pMarketProductTarget);
    }

    saveMarketProductTargetReport(pMarketTargetReport: MarketProductTarget, pURL: string): Observable<any> {
        return this.postDataForMicroService(pURL + (pMarketTargetReport.id ? Endpoints.update_market_product_target_report : Endpoints.create_market_product_target_report), pMarketTargetReport).pipe(map(response => response));
    }

    updateActiveStatus(pActive: any, pURL: string): Observable<any> {
        return this.postDataForMicroService(pURL + Endpoints.market_product_target_report_update_active_status, pActive);
    }

    // tertiary sales


    // tour plan
    getTourTypeList(pParameters: any): Observable<TourType[]> {
        return this.postData(Endpoints.tour_type_list, pParameters).pipe(map(response => response['Data']['content']));
    }

    getTourTypeDetails(pTourTypeId: number): Observable<TourType> {
        return this.getData(`${Endpoints.tour_type_details}/${pTourTypeId}`).pipe(map(response => response['Data']['TourType']));
    }

    saveTourType(pTourType: TourType): Observable<any> {
        return this.postData(pTourType.Id ? Endpoints.tour_type_edit : Endpoints.tour_type_add, pTourType);
    }

    getTourPlanList(pParameters: any): Observable<TourPlanUser[]> {
        return this.postData(Endpoints.tour_plan_user_list, pParameters).pipe(map(response => response['Data']['content']));
    }

    getTourPlanDetails(pTourPlanId: number): Observable<TourPlan> {
        return this.getData(`${Endpoints.tour_plan_details}/${pTourPlanId}`).pipe(map(response => response['Data']['TourPlan']));
    }

    getTourPlanDetailsByUserYearMonth(pUserId: number, pYear: number, pMonth: number): Observable<TourPlan> {
        return this.getData(`${Endpoints.tour_plan_details_by_user_year_month}?UserId=${pUserId}&Year=${pYear}&Month=${pMonth}`)
            .pipe(map(response => response['Data']['TourPlan']));
    }

    getTourPlanHistory(pUserId: number, pYear: number, pMonth: number): Observable<any> {
        return this.getData(`${Endpoints.tour_plan_history}?UserId=${pUserId}&Year=${pYear}&Month=${pMonth}`)
            .pipe(map(response => response['Data']['content']));
    }

    saveTourPlan(pTourPlan: TourPlan): Observable<any> {
        return this.postData(pTourPlan.Id ? Endpoints.tour_plan_edit : Endpoints.tour_plan_add, pTourPlan);
    }

    saveTourPlanTarget(pTourPlanTarget: TourPlanTarget): Observable<any> {
        return this.postData(pTourPlanTarget.Id ? Endpoints.tour_plan_target_edit : Endpoints.tour_plan_target_add, pTourPlanTarget);
    }

    approveTourPlan(pApproval: Approval): Observable<any> {
        return this.postData(Endpoints.tour_plan_approve, pApproval);
    }

    approveTourPlanItemWise(pApproval: ItemWiseTourApproval): Observable<any> {
        return this.postData(Endpoints.item_wise_tour_plan_approve, pApproval);
    }

    // pjp plan

    getPjpPlanDetailsByUserYearMonth(pUserId: number, pYear: number, pMonth: number): Observable<PjpPlan> {
        return this.getDataForMicroService(`f-party/${Endpoints.pjp_plan_details_by_user_year_month}/${pUserId}/${pYear}/${pMonth}`)
            .pipe(map(response => response['data']));
    }

    addSingleTourPlan(pPjpPlan: PjpPlanRequest, pMicroServiceUrl: string): Observable<any> {
        return this.postDataForMicroService(pMicroServiceUrl + Endpoints.pjp_add_single_tour_plan, pPjpPlan);
    }

    addSingleTeamTourPlan(pPjpPlan: PjpPlanRequestForSingleTeam, pMicroServiceUrl: string): Observable<any> {
        return this.postDataForMicroService(pMicroServiceUrl + Endpoints.pjp_add_team_tour_plan, pPjpPlan);
    }

    deleteSingleTourPlan(pId: number, pMicroServiceUrl: string): Observable<any> {
        return this.deleteDataForMicroservice(`${pMicroServiceUrl}${Endpoints.pjp_delete_single_tour_plan}/${pId}`);
    }

    // training
    getTrainingDetails(pId: number): Observable<Training> {
        return this.getData(`${Endpoints.training_details}/${pId}`).pipe(map(response => response['Data']['Training']));
    }

    saveTraining(pTraining: Training): Observable<Training> {
        return this.postData(pTraining.Id ? Endpoints.training_edit : Endpoints.training_add, pTraining);
    }

    // route plan

    getRoutePlanDetailsByUserYearMonth(pUserId: number, pYear: number, pMonth: number): Observable<RoutePlan> {
        return this.getDataForMicroService(`f-party/${Endpoints.route_plan_details_by_user_year_month}/${pUserId}/${pYear}/${pMonth}`)
            .pipe(map(response => response['data']));
    }

    updateSingleRoutePlan(pPjpPlan: RoutePlanRequest, pMicroServiceUrl: string): Observable<any> {
        return this.postDataForMicroService(pMicroServiceUrl + Endpoints.update_single_route_plan, pPjpPlan);
    }

    updateTeamRoutePlan(pPjpPlan: RoutePlanRequest, pMicroServiceUrl: string): Observable<any> {
        return this.postDataForMicroService(pMicroServiceUrl + Endpoints.update_team_route_plan, pPjpPlan);
    }

    deleteSingleRoutePlan(pId: number, pMicroServiceUrl: string): Observable<any> {
        return this.deleteDataForMicroservice(`${pMicroServiceUrl}${Endpoints.route_delete_single_tour_plan}/${pId}`);
    }

    // transport

    getTransportModeList(pParameters: any): Observable<TransportMode[]> {
        return this.postData(Endpoints.transport_mode_list, pParameters).pipe(map(response => response['Data']['content']));
    }

    saveTransportMode(pTransportMode: TransportMode): Observable<any> {
        return this.postData(pTransportMode.Id ? Endpoints.transport_mode_edit : Endpoints.transport_mode_add, pTransportMode);
    }


    // user

    getModuleListForUserRole(pParameters: any): Observable<Module[]> {
        return this.postData(Endpoints.user_role_modules, pParameters).pipe(map(response => response['Data']['content']));
    }

    getFoodBill(pUserId: number, pFromDate: string, pToDate: string): Observable<any[]> {
        return this.getData(`${Endpoints.food_bill}` + '?' + ('access_token=' + this.accessToken + '&') + 'UserId=' + pUserId + '&' + 'FromDate=' + pFromDate + '&' + 'ToDate=' + pToDate).pipe(map(response => response['Data']['content']));
    }

    changeUserStatus(pApproval: UserStatusChange): Observable<any> {
        return this.postData(Endpoints.change_user_status, pApproval);
    }

    approveUser(pApproval: Approval): Observable<any> {
        return this.postData(Endpoints.user_approve, pApproval);
    }

    saveUserRole(pUserRole: UserRoleForMicroServiceManage, pMicroServiceUrl: string): Observable<any> {
        return this.postDataForMicroService(pMicroServiceUrl + (pUserRole.id ? Endpoints.user_role_edit : Endpoints.user_role_add), pUserRole);
    }

    saveUserRolePermissions(pUserRolePermissionManage: UserRolePermissionManage, pMicroServiceUrl: string): Observable<any> {
        return this.postDataForMicroService(pMicroServiceUrl + Endpoints.update_role_authorities, pUserRolePermissionManage);
    }

    getUserList(pParameters: any): Observable<User[]> {
        return this.postData(Endpoints.user_list, pParameters).pipe(map(response => response['Data']['content']));
    }

    getUserStock(pParameters?: any): Observable<any[]> {
        return this.postData(Endpoints.user_stock, pParameters).pipe(map(response => response['Data']['content']));
    }


    getUserAttendanceReference(pId: any): Observable<any> {
        return this.getData(`${Endpoints.user_attendance_reference}/${pId}`).pipe(map(response => response['Data']['Reference']));
    }

    getDistributorListForFilter(pParameters: any): Observable<User[]> {
        return this.postData(Endpoints.distributor_list_for_filter, pParameters).pipe(map(response => response['Data']['content']));
    }

    getRetailerListForFilter(pParameters: any): Observable<User[]> {
        return this.postData(Endpoints.retailer_list_for_filter, pParameters).pipe(map(response => response['Data']['content']));
    }

    getProjectListForFilter(pParameters: any): Observable<Project[]> {
        return this.postData(Endpoints.project_list_for_filter, pParameters).pipe(map(response => response['Data']['content']));
    }

    getUserListByAccessibleMarket(pParameters: any): Observable<User[]> {
        return this.postData(Endpoints.user_list_by_accessible_market, pParameters).pipe(map(response => response['Data']['content']));
    }

    getUserDetails(pUserId: number): Observable<User> {
        return this.getData(`${Endpoints.user_details}/${pUserId}`).pipe(map(response => response['Data']['User']));
    }


    saveUser(pUser: User): Observable<any> {
        return this.postData(pUser.Id ? Endpoints.user_edit : Endpoints.user_add, pUser);
    }

    deleteUserDevice(pDeviceId: number): Observable<any> {
        return this.deleteData(`${Endpoints.device_remove}/${pDeviceId}`);
    }

    deleteUserDeviceForMicroService(pDeviceId: number, pMicroServiceUrl: string): Observable<any> {
        return this.deleteDataForMicroservice(`${pMicroServiceUrl}${Endpoints.device_remove_for_micro_service}/${pDeviceId}`);
    }

    deleteUserLocation(pUserId: number): Observable<any> {
        return this.deleteData(`${Endpoints.user_location_delete}/${pUserId}`);
    }

    getNotificationList(pParameters: any): Observable<NotificationMinute[]> {
        return this.postData(Endpoints.notification_list, pParameters).pipe(map(response => response['Data']['content']));
    }

    getUserRoleList(pParameters: any): Observable<UserRole[]> {
        return this.postData(Endpoints.user_role_list, pParameters).pipe(map(response => response['Data']['content']));
    }

    getSrList(pParameters: any, pMicroserviceUrl: string): Observable<Sr[]> {
        return this.postDataForMicroService(pMicroserviceUrl + Endpoints.sr_list, pParameters).pipe(map(response => response['data']['content']));
    }

    getUserRoleListForMicroservice(pParameters: any, pMicroserviceUrl: string): Observable<UserRoleForMicroServiceList[]> {
        return this.postDataForMicroService(pMicroserviceUrl + Endpoints.user_role_detailed_list, pParameters).pipe(map(response => response['data']['content']));
    }

    getUserRoleDetails(pUserRoleId: number): Observable<UserRole> {
        return this.getData(`${Endpoints.user_role_details}/${pUserRoleId}`).pipe(map(response => response['Data']['UserRole']));
    }

    getUserRoleDetailsV2(pUserRoleId: number, pMicroServiceUrl: string): Observable<UserRoleForMicroServiceList> {
        return this.getDataForMicroService(pMicroServiceUrl + `${Endpoints.user_role_details_v2}?role_id=${pUserRoleId}`).pipe(map(response => response['data']));
    }

    getUserRolePermissions(pOrgId: number, pMicroServiceUrl: string): Observable<UserRolePermissionList[]> {
        return this.getDataForMicroService(pMicroServiceUrl + `${Endpoints.user_role_permission_list}?org_id=${pOrgId}`).pipe(map(response => response['data']['content']));
    }

    getUserMarketList(pParameters: any): Observable<Market[]> {
        return this.postData(Endpoints.market_list_of_user, pParameters).pipe(map(response => response['Data']['content']));
    }

    getRetailerListOfUser(pParameters: any): Observable<Customer[]> {
        return this.postData(Endpoints.retailer_list_of_user, pParameters).pipe(map(response => response['Data']['content']));
    }

    getSubordinateUserRoles(microServiceUrl: string): Observable<UserRoleRef[]> {
        return this.getDataForMicroService(microServiceUrl + Endpoints.subordinate_user_role_list).pipe(map(response => response['data']['content']));
    }

    getTeamUserFilterList(params: any, microServiceUrl: string): Observable<any> {
        return this.postDataForMicroService(microServiceUrl + Endpoints.team_user_filter_list, params).pipe(map(response => response['data']['content']));
    }

    getMovementUserRoleList(params: any, microServiceUrl: string): Observable<any> {
        return this.getDataForMicroService(microServiceUrl + Endpoints.movement_user_role_list, params).pipe(map(response => response['data']['content']));
    }


    // visit
    saveUserRoleWiseVisitTarget(pUserRoleWiseVisitTarget: UserRoleWiseVisitTarget): Observable<any> {
        return this.postData(pUserRoleWiseVisitTarget.Id ? Endpoints.edit_user_role_wise_visit_target : Endpoints.add_user_role_wise_visit_target, pUserRoleWiseVisitTarget);
    }

    deleteUserRoleWiseVisitTarget(pVisitTargetId: number): Observable<any> {
        return this.deleteData(`${Endpoints.delete_user_role_wise_visit_target}/${pVisitTargetId}`);
    }


    getVisitTypeList(pParameters: any): Observable<VisitType[]> {
        return this.postData(Endpoints.visit_type_list, pParameters).pipe(map(response => response['Data']['content']));
    }

    getVisitTypeDetails(pVisitTypeId: number): Observable<VisitType> {
        return this.getData(`${Endpoints.visit_type_details}/${pVisitTypeId}`).pipe(map(response => response['Data']['VisitType']));
    }

    saveVisitType(pVisitType: VisitType): Observable<any> {
        return this.postData(pVisitType.Id ? Endpoints.visit_type_edit : Endpoints.visit_type_add, pVisitType);
    }


    getVisitStatusList(pParameters: any): Observable<VisitStatus[]> {
        return this.postData(Endpoints.visit_status_list, pParameters).pipe(map(response => response['Data']['content']));
    }

    getVisitStatusDetails(pVisitStatusId: number): Observable<VisitStatus> {
        return this.getData(`${Endpoints.visit_status_details}/${pVisitStatusId}`).pipe(map(response => response['Data']['VisitStatus']));
    }

    saveVisitStatus(pVisitStatus: VisitStatus): Observable<any> {
        return this.postData(pVisitStatus.Id ? Endpoints.visit_status_edit : Endpoints.visit_status_add, pVisitStatus);
    }

    getVisitList(pParameters: any): Observable<Visit[]> {
        return this.postData(Endpoints.visit_list, pParameters).pipe(map(response => response['Data']['content']));
    }

    getVisitDetails(pVisitId: number): Observable<Visit> {
        return this.getData(`${Endpoints.visit_details}/${pVisitId}`).pipe(map(response => response['Data']['Visit']));
    }

    saveVisit(pVisit: Visit): Observable<any> {
        return this.postData(pVisit.Id ? Endpoints.visit_edit : Endpoints.visit_add, pVisit);
    }

    getVisitTypeFieldNameList(pParameters: any): Observable<any> {
        return this.postData(Endpoints.visit_type_field_name_list, pParameters);
    }

    approveVisit(pApproval: Approval): Observable<any> {
        return this.postData(Endpoints.visit_approve, pApproval);
    }

    deleteVisitSummaryApprovalRule(pVisitSummaryRuleId: number): Observable<any> {
        return this.deleteData(`${Endpoints.visit_summary_approval_delete}/${pVisitSummaryRuleId}`);
    }


    getVisitSummaryApprove(pVisitSummaryRuleId: number): Observable<VisitSummaryApprovalRule> {
        return this.getData(`${Endpoints.visit_summary_approval_details}/${pVisitSummaryRuleId}`)
            .pipe(map(response => response['Data']['PaymentApprovalRule']));
    }


    saveVisitSummaryApproveRule(pVisitSummaryApproveRule: VisitSummaryApprovalRule): Observable<VisitSummaryApprovalRule> {
        return this.postData(pVisitSummaryApproveRule.Id ? Endpoints.visit_summary_approval_edit : Endpoints.visit_summary_approval_add, pVisitSummaryApproveRule);
    }


    // attendance report

    // dealer report

    // distributor stock report
    getDistributorStockReport(pParameters: any): Observable<ProductGroupStockSummary[]> {
        return this.postData(Endpoints.distributor_stock_report, pParameters).pipe(map(response => response['Data']['content']));
    }

    getDistributorStockList(pParameters: any): Observable<DistributorStock[]> {
        return this.postData(Endpoints.distributor_stock_list, pParameters).pipe(map(response => response['Data']['content']));
    }

    saveDistributorStock(pParameters: any): Observable<any> {
        return this.postData(Endpoints.distributor_stock_adjust, pParameters);
    }


    // expense claim report
    deleteExpenseClaimApprovalRule(pExpenseClaimApprovalRuleId: number): Observable<any> {
        return this.deleteData(`${Endpoints.expenseClaim_approval_delete}/${pExpenseClaimApprovalRuleId}`);
    }

    saveExpenseClaimApproveRule(pExpenseClaimApproveRule: ExpenseClaimApprovalRules): Observable<ExpenseClaimApprovalRules> {
        return this.postData(pExpenseClaimApproveRule.Id ? Endpoints.expenseClaim_approval_edit : Endpoints.expenseClaim_approval_add, pExpenseClaimApproveRule);
    }

    getExpenseClaimApprove(pExpenseClaimRuleId: number): Observable<ExpenseClaimApprovalRules> {
        return this.getData(`${Endpoints.expenseClaim_approval_details}/${pExpenseClaimRuleId}`)
            .pipe(map(response => response['Data']['PaymentApprovalRule']));
    }


    // kpi report
    getPersonWiseMonthlyKPI(pId: number): Observable<PersonWiseMonthlyKpi> {
        return this.postData(Endpoints.person_wise_monthly_kpi_report, {Id: pId})
            .pipe(map(response => response['Data']['content'] ? response['Data']['content'][0] : null));
    }


    // leave report
    getLeaveReport(pParameters: any): Observable<UserLeaveAccount[]> {
        return this.postData(Endpoints.user_leave_account, pParameters).pipe(map(response => response['Data']['content']));
    }


    // visit summary
    getDistributorEngagementDetails(pEngagementId: number): Observable<Engagement> {
        return this.getData(`${Endpoints.distributor_engagement_details}/${pEngagementId}`).pipe(map(response => response['Data']['Engagement']));
    }

    getCustomerEngagementDetails(pEngagementId: number): Observable<Engagement> {
        return this.getData(`${Endpoints.customer_engagement_details}/${pEngagementId}`).pipe(map(response => response['Data']['Engagement']));
    }

    getProjectEngagementDetails(pEngagementId: number): Observable<Engagement> {
        return this.getData(`${Endpoints.project_engagement_details}/${pEngagementId}`).pipe(map(response => response['Data']['Engagement']));
    }

    approveDistributorEngagement(pApproval: Approval): Observable<any> {
        return this.postData(Endpoints.distributor_engagement_approve, pApproval);
    }

    approveCustomerEngagement(pApproval: Approval): Observable<any> {
        return this.postData(Endpoints.customer_engagement_approve, pApproval);
    }

    approveProjectEngagement(pApproval: Approval): Observable<any> {
        return this.postData(Endpoints.project_engagement_approve, pApproval);
    }


    // notification rule report
    setNotificationRuleUser(pNotificationRule: NotificationRule): Observable<any> {
        return this.postData(pNotificationRule.Id ? Endpoints.edit_notification_rule : Endpoints.create_notification_rule, pNotificationRule);
    }

    deleteNotificationRule(pNotificationRuleId: number): Observable<any> {
        return this.deleteData(`${Endpoints.delete_notification_rule}/${pNotificationRuleId}`);
    }


    // others
    utilityUpload(file: FormData): Observable<any> {
        return this.postData(Endpoints.utility_upload, file);
    }

    utilityUploadForCategory(category: string, file: FormData): Observable<any> {
        return this.postData(`${Endpoints.utility_upload}/${category}`, file);
    }

    getFileDetails(pId: string): Observable<Fil> {
        return this.getFile(base_urls.sfa_url + `${pId}/info`).pipe(map(response => response['Data']['FileInfo']));
    }

    // approval rules

    saveApprovalRules(pApprovalRule: ApprovalRuleRequest, pURL: string): Observable<any> {
        return this.postDataForMicroService(pURL + (pApprovalRule.id ?  Endpoints.approval_rule_edit : Endpoints.approval_rule_add), pApprovalRule).pipe(map(response => response['data']));
    }

    // da rule
    saveDaRule(pDaRule: DaRuleManage, pURL: string): Observable<any> {
        return this.postDataForMicroService(pURL + Endpoints.da_rule_manage, pDaRule).pipe(map(response => response['data']));
    }

    removeDaRule(pId: number, pURL: string): Observable<DaRuleManage> {
        return this.deleteDataForMicroservice(pURL + `${Endpoints.da_rule_remove}/${pId}`).pipe(map(response => response['data']));
    }


    // retailer sales

    getRetailerSalesDetails(pParameters: any, pURL: string): Observable<any[]> {
        return this.postDataForMicroService(pURL + Endpoints.retailer_wise_details_sales_report, pParameters).pipe(map(response => response['data']['content']));
    }

    getMarketAndUserColumns(pParameters: any, pURL: string): Observable<any[]> {
        return this.postDataForMicroService(pURL + Endpoints.market_and_user_column, pParameters).pipe(map(response => response['data']['content']));
    }

    // ims

    getImsDetails(pId: number, pURL: string): Observable<Ims> {
        return this.getDataForMicroService(pURL + `${Endpoints.ims_details}/${pId}`, {}).pipe(map(response => response['data']));
    }

    approveIms(pApproval: ApprovalForMicroService,  pURL: string): Observable<any> {
        return this.postDataForMicroService(pURL + Endpoints.ims_approve, pApproval);
    }

    editIms(pParameters: any, pURL: string): Observable<any> {
        return this.postDataForMicroService(pURL + Endpoints.ims_edit, pParameters).pipe(map(response => response['data']));
    }

    getImsDetailsReport(param, url): Observable<any> {
        return this.postDataForMicroService(`${url}${Endpoints.ims_details_report}`, param).pipe(map(response => response['data']));
    }

    getIMSDailySummaryByVolume(pParameters: any, pURL): Observable<ImsDailySummaryByVolume[]> {
        return this.postDataForMicroService(pURL + Endpoints.ims_daily_summary_by_volume_list, pParameters).pipe(map(response => response['data']['content']));
    }

    updateImsToAdjustStock(pParameters: any, pURL): Observable<any> {
        return this.postDataForMicroService(pURL + Endpoints.update_ims_to_adjust_stock, pParameters).pipe(map(response => response['data']['id']));
    }

    // asset

    getAssetTypeList(params: any, microServiceUrl: string): Observable<any> {
        return this.getDataForMicroService(microServiceUrl + Endpoints.asset_type_list, params).pipe(map(response => response['data']['content']));
    }

    getAssetList(params: any, microServiceUrl: string): Observable<any> {
        return this.getDataForMicroService(microServiceUrl + Endpoints.asset_list, params).pipe(map(response => response['data']['content']));
    }

    uploadRetailerAsset(pUploadedProduct: AssetRetailerUpload, pURL: string): Observable<any> {
        return this.postDataForMicroService(pURL + Endpoints.asset_retailer_upload, pUploadedProduct).pipe(map(response => response));
    }

    getAssetListRetailer(params: any, microServiceUrl: string): Observable<any> {
        return this.getDataForMicroService(microServiceUrl + Endpoints.asset_list_retailer, params).pipe(map(response => response['data']['content']));
    }

    generateExportStatus(pRequestId: string, pURL: string): Observable<any> {
        return this.getDataForMicroService(pURL + `${Endpoints.generate_export_status}?request_id=${pRequestId}`).pipe(map(response => response['data']));
    }

    getNonProductiveTotal(pFilters: any, pURL: string): Observable<any> {
        return this.postDataForMicroService(pURL + Endpoints.non_productive_retailer_report_total, pFilters).pipe(map(response => response['data']));
    }

    // weekly buyer outlet

    getWeeklyBuyerOutletTotal(pFilters: any, pURL: string): Observable<WeeklyBuyerOutletTotal> {
        return this.postDataForMicroService(pURL + Endpoints.weekly_buyer_outlet_total, pFilters).pipe(map(response => response['data']));
    }

}
